import { readChatTurns } from "@/lib/chat-turn-history";
import {chatDraftKey, readChatDraft} from "@/lib/chat-drafts";
import {restoreChatTools} from "@/lib/chat-tool-history";
import { readAllRows } from "@/lib/read-all-rows";
import {recoverChatMessages} from '@/lib/chat-recovery';
import {applyChatProgress} from '@/lib/chat-progress';
import {chatRetrySubmission,chatFailureHeadline} from "@/lib/chat-retry";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useRef, useEffect, useLayoutEffect, useCallback, type FormEvent, type DragEvent } from "react";
import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Send, Paperclip, Sparkles, BookOpen, Plus, FolderOpen, X, MoreHorizontal, FolderInput, BookMarked, PanelRight, ChevronLeft, Share2, Eye, Drama, Square } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChatMessages, type ChatMessage, type ToolEvent } from "@/components/chat/chat-messages";
import { SkillsPanel, type Skill } from "@/components/chat/skills-panel";
import { ActiveSkillsBar } from "@/components/chat/active-skills-bar";
import { ModelModeToggle } from "@/components/chat/model-mode-toggle";
import { PromptsPanel } from "@/components/chat/prompts-panel";
import { PromptQuickPicker } from "@/components/chat/prompt-quick-picker";
import { KnowledgePanel, type KnowledgePack } from "@/components/chat/knowledge-panel";
import { ActiveKnowledgeBar } from "@/components/chat/active-knowledge-bar";
import { PersonasPanel, type Persona } from "@/components/chat/personas-panel";
import { ActivePersonaBar } from "@/components/chat/active-persona-bar";
import { PersonaChip } from "@/components/chat/persona-chip";
import { PersonaQuickPicker } from "@/components/chat/persona-quick-picker";
import { CONVERSATIONS_CHANGED_EVENT } from "@/components/navigation-events";
import { AttachmentChips, type ChipItem } from "@/components/chat/attachment-chips";
import { RightPane } from "@/components/chat/right-pane";
import { ChatEmptyState } from "@/components/chat/chat-empty-state";
import { WhatsThis } from "@/components/help/whats-this";
import { ProjectPickerDialog } from "@/components/projects/project-picker-dialog";
import type { Insight } from "@/components/chat/insights-tab";
import type { FileCardData } from "@/components/chat/file-card";
import { uploadChatFile, getSignedUrl, retryExtraction, MAX_FILE_BYTES, MAX_FILES_PER_MESSAGE, type UploadedAttachment } from "@/lib/upload";
import { estimateTokens } from "@/lib/tokens";
import { aws as awsClient, getCsrfToken } from "@/aws/client";
import { toast } from "sonner";
import { ShareDialog } from "@/components/share/share-dialog";
import { useResourceRole } from "@/hooks/use-resource-role";
import { useWorkflowSuggestion } from "@/hooks/use-workflow-suggestion";
import { WorkflowSuggestionChip } from "@/components/chat/workflow-suggestion-chip";
import { startRun as startWorkflowRun, startAgenticRun, suggestWorkflow } from "@/features/workflows/workflow.functions";
import { useServerFn } from "@/lib/use-server-fn";
import { JobProgressCard } from "@/components/chat/job-progress-card";
import { useMemo } from "react";
import { observeChatFollowups, type FollowupEntry } from "@/lib/chat-followups";
import { streamChatTurn, cancelChatTurn, type ChatFinalResult, type ChatStartInput } from "@/lib/chat-turn-stream";
import { operation } from "@/lib/api-client";
import { readDroppedWorkspaceTree, type DroppedWorkspaceTree } from "@/lib/dropped-folder";
import { workspaceApi } from "@/lib/workspace";

type ChatSearch = { conversation?: string; prefill?: string };

const RIGHT_PANE_DEFAULT_SIZE = 25;
const RIGHT_PANE_MIN_SIZE = 18;
const RIGHT_PANE_MAX_SIZE = 40;
const RIGHT_PANE_SIZE_STORAGE_KEY = "askbell.rightPaneSize";

function readRightPaneSize() {
  if (typeof window === "undefined") return RIGHT_PANE_DEFAULT_SIZE;
  try {
    const stored = localStorage.getItem(RIGHT_PANE_SIZE_STORAGE_KEY);
    if (stored === null) return RIGHT_PANE_DEFAULT_SIZE;
    const saved = Number(stored);
    if (Number.isFinite(saved)) return Math.min(RIGHT_PANE_MAX_SIZE, Math.max(RIGHT_PANE_MIN_SIZE, saved));
  } catch { /* ignore */ }
  return RIGHT_PANE_DEFAULT_SIZE;
}

export const Route = createFileRoute("/chat")({
  validateSearch: (raw: Record<string, unknown>): ChatSearch => {
    const out: ChatSearch = {};
    const c = raw.conversation;
    if (typeof c === "string" && c.length > 0) out.conversation = c;
    const p = raw.prefill;
    if (typeof p === "string" && p.length > 0) out.prefill = p.slice(0, 4000);
    return out;
  },
  head: () => ({
    meta: [
      { title: "AskBell Chat" },
      { name: "description", content: "AI-powered chat for Bellomy staff" },
    ],
  }),
  component: ChatPage,
});

export type { ChatMessage } from "@/components/chat/chat-messages";





type Pending = ChipItem & {
  uploaded?: UploadedAttachment;
  workspaceImport?: { chatId: string; rootNodeIds: string[]; workspaceRevision: number };
};

// Same threshold as fresh uploads. Above this, we send compact metadata
// pointing at the sandbox-mounted file instead of inlining the raw text,
// to prevent old conversations from re-sending giant spreadsheet dumps
// on every follow-up question.
// Historical turns prefer the compact <attached_file> hint over re-inlining
// large extracted text on every follow-up message. Fresh uploads still use the
// larger INLINE_TEXT_LIMIT (see handleSend), so this only affects replay.
const HISTORY_INLINE_TEXT_LIMIT = 4_000;
const safeFilename = (s: string) => s.replace(/[^a-zA-Z0-9._-]/g, "_");

function toSafeDisplayName(value: unknown, fallback = "Collaborator"): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || fallback;
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value == null) return fallback;

  let serialized = "[unserializable]";
  try {
    serialized = JSON.stringify(value);
  } catch {
    // no-op: keep fallback marker
  }
  console.warn("[chat:invalid-display-name]", { type: typeof value, serialized });
  return fallback;
}

/**
 * Build the model-facing user content for a historical user message.
 * - small attachments: inline extracted text (preserves analysis fidelity)
 * - large attachments: compact <attached_file> hint + sandbox path
 * - no rawAttachments hydrated: fall back to augmented_content ?? content
 */
function buildHistoricalUserContent(m: ChatMessage): string {
  if (!m.rawAttachments || m.rawAttachments.length === 0) {
    return m.augmented_content ?? m.content;
  }
  let out = m.content || "(see attached files)";
  for (const a of m.rawAttachments) {
    if (a.mime_type.startsWith("image/")) continue; // images skipped here; not re-sent on history replay
    if (a.extracted_text && a.extracted_text.length <= HISTORY_INLINE_TEXT_LIMIT) {
      out += `\n\n--- Attached: ${a.filename} ---\n${a.extracted_text}\n--- end ${a.filename} ---`;
    } else {
      const path = `/home/user/inputs/${safeFilename(a.filename)}`;
      const preview = a.extracted_text
        ? `\nFirst ${Math.min(500, a.extracted_text.length)} chars (preview only):\n${a.extracted_text.slice(0, 500)}…`
        : "";
      const sizeKb = Math.round(a.size / 1024);
      out += `\n\n<attached_file>\nfilename: ${a.filename}\nmime_type: ${a.mime_type}\nsize: ${sizeKb} KB\nsandbox_path: ${path}\nThe full file content is too large to include inline. To work with this file, call run_python and read it from "${path}" (e.g. with pandas: pd.read_excel("${path}") or pd.read_csv("${path}")). Do NOT ask the user to re-paste the data.${preview}\n</attached_file>`;
    }
  }
  return out;
}

function ChatPage() {
  const { user } = useAuth();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/chat" });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Mirror of `messages` for handlers that need the freshest value across
  // Stop → resubmit within the same render cycle (avoids stale closures that
  // caused duplicate user bubbles in the outgoing request body).
  const messagesRef = useRef<ChatMessage[]>([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  // Brief visual confirmation when Stop is clicked. Flips true synchronously
  // in handleStop and clears after ~450ms so the button unambiguously
  // acknowledges the click even before React commits any other state.
  const [isStopping, setIsStopping] = useState(false);
  // One synchronously-owned turn prevents double submit and ensures an older
  // async invocation can never clear or cancel a newer one.
  type ActiveTurn = {
    id: string;
    kind: "send" | "retry";
    phase: "preparing" | "streaming" | "cleaning";
    controller: AbortController;
    cancelled: boolean;
    stopRequested?: boolean;
    cleanupMessageId?: string | null;
    userMsgId?: string;
    assistantId: string;
    originalInput?: string;
    originalPending?: Pending[];
    convId?: string | null;
    userMessageRowId?: string | null;
    clientRequestId: string;
    /** Server-side turn id (chat_turns) — lets Stop cancel a background model run. */
    serverTurnId?: string | null;
  };
  const activeTurnRef = useRef<ActiveTurn | null>(null);
  // Mirror of the current turn's controller so Stop can always abort even if
  // the finally block has already cleared activeTurnRef.
  const streamAbortRef = useRef<AbortController | null>(null);
  useEffect(()=>()=>{loadTokenRef.current++;const turn=activeTurnRef.current;if(turn){turn.cancelled=true;turn.controller.abort();}activeTurnRef.current=null;streamAbortRef.current=null;},[]);
  // Set to the id of a conversation we just created inside ensureConversation()
  // so the URL→loadConversation effect does not re-hydrate over the messages
  // that are actively streaming into local state.
  const justCreatedConvRef = useRef<string | null>(null);
  const [streamStartedAt, setStreamStartedAt] = useState<number | null>(null);
  const [streamElapsed, setStreamElapsed] = useState(0);
  useEffect(() => {
    if (!isStreaming) { setStreamStartedAt(null); setStreamElapsed(0); return; }
    const start = Date.now();
    setStreamStartedAt(start);
    setStreamElapsed(0);
    const id = setInterval(() => setStreamElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [isStreaming]);
  const [activeTab, setActiveTab] = useState("chat");
  const isMobile = useIsMobile();
  const [insightsSheetOpen, setInsightsSheetOpen] = useState(false);
  const [rightPaneCollapsed, setRightPaneCollapsedState] = useState(false);
  const rightPaneSizeRef = useRef(readRightPaneSize());
  useEffect(() => {
    try {
      setRightPaneCollapsedState(localStorage.getItem("askbell.rightPaneCollapsed") === "1");
    } catch { /* ignore */ }
  }, []);
  const setRightPaneCollapsed = (v: boolean) => {
    setRightPaneCollapsedState(v);
    try { localStorage.setItem("askbell.rightPaneCollapsed", v ? "1" : "0"); } catch { /* ignore */ }
  };

  const [conversationId, setConversationId] = useState<string | null>(null);
  // Id of a conversation currently being hydrated from the DB (shows a
  // loading state so clicking a sidebar chat feels instant).
  const [loadingConvId, setLoadingConvId] = useState<string | null>(null);
  const [conversationLoadError,setConversationLoadError]=useState<string|null>(null);
  // Monotonic token so a slow load can't stomp a newer one.
  const loadTokenRef = useRef(0);
  const projectPickRef = useRef(0);
  const followupsRef = useRef<(result:ChatFinalResult,userText:string,memoryText:string)=>Promise<void>>(async()=>{});
  const followupObserversRef = useRef(new Set<string>());
  const notifiedMemoriesRef = useRef(new Set<string>());
  // Background jobs started from this conversation — rendered as live cards.
  const [jobRunIds, setJobRunIds] = useState<string[]>([]);
  // Bumped on each send so finished job cards clear themselves out of the way.
  const [clearFinishedJobsToken, setClearFinishedJobsToken] = useState(0);
  const dismissJob = useCallback(async (rid:string) => {
    try {
      await operation("workflow_dismiss",{id:rid},getCsrfToken());
      setJobRunIds(ids=>ids.filter(id=>id!==rid));
    } catch(error) {toast.error(error instanceof Error ? error.message : "Couldn't dismiss this job.");}
  }, []);
  const [pending, setPending] = useState<Pending[]>([]);
  const [workspaceRefreshKey, setWorkspaceRefreshKey] = useState(0);
  const [workspaceSelectedFolderId, setWorkspaceSelectedFolderId] = useState<string | null>(null);
  const [workspaceRevealNodeId, setWorkspaceRevealNodeId] = useState<string | null>(null);
  useEffect(() => {
    setWorkspaceSelectedFolderId(null);
    setWorkspaceRevealNodeId(null);
  }, [conversationId]);

  const [isDragging, setIsDragging] = useState(false);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState<{ message: string; details?: string | null } | null>(null);
  const [historyVersion, setHistoryVersion] = useState(0);
  // Notify the sidebar (and anyone else listening) whenever conversations change.
  useEffect(() => {
    if (historyVersion === 0) return;
    window.dispatchEvent(new Event(CONVERSATIONS_CHANGED_EVENT));
  }, [historyVersion]);
  const [retryingMessageId, setRetryingMessageId] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const { role: chatRole, loading:roleLoading, error:roleError, refresh:refreshRole } = useResourceRole(conversationId ? "chat" : null, conversationId);
  const isViewer = chatRole === "viewer";
  const readOnlyChat = Boolean(conversationId) && chatRole!=="owner" && chatRole!=="collaborator";
  const canEditChatContext = !loadingConvId && !conversationLoadError && (!conversationId || (!roleLoading && chatRole === "owner"));
  // Project context for the current chat (chip in header + picker for new chats)
  const [currentProject, setCurrentProject] = useState<{ id: string; name: string } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // When true, picker is acting on the existing conversation ("Move to project…")
  // rather than choosing the project for the next new chat.
  const [movePickerOpen, setMovePickerOpen] = useState(false);
  // null = unset (will trigger picker on first new chat); otherwise the picked project id (or null for standalone)
  const [pendingProjectId, setPendingProjectId] = useState<string | null | undefined>(undefined);
  // Skills enabled for the current conversation. Persisted in conversation_skills
  // once a conversation exists; held locally before the first send.
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  const [skillMode, setSkillMode] = useState<"manual" | "auto">("auto");
  const [modelMode, setModelMode] = useState<"fast" | "thinking">("thinking");
  // Optional explicit model override (right-click on the toggle). null = use mode default.
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  // Knowledge packs enabled for the current conversation. Persisted in
  // conversation_knowledge once a conversation exists; held locally before first send.
  const [allKnowledge, setAllKnowledge] = useState<(Pick<KnowledgePack,"id"|"title"> & {content?:string;content_length?:number})[]>([]);
  const [selectedKnowledgeIds, setSelectedKnowledgeIds] = useState<string[]>([]);
  // Active persona for the current conversation. Persisted in conversation_personas
  // once a conversation exists; held locally before first send. Single-select — only
  // one persona can be active per conversation. When set, the persona's system_prompt
  // FULLY REPLACES the default AskBell constitution server-side.
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
  const [allPersonas, setAllPersonas] = useState<Persona[]>([]);
  // Tracks whether the user has manually picked/changed/cleared the persona for the
  // current draft chat. When true, we never auto-apply a project's default persona.
  const userTouchedPersonaRef = useRef(false);
  // Skills the model auto-loaded mid-turn (in auto mode), for badge display in ActiveSkillsBar
  const [autoLoadedSkillIds, setAutoLoadedSkillIds] = useState<Set<string>>(new Set());
  const lastSendRef = useRef<{ contentParts: any[]; userText: string; convId: string | null; apiMessages: any[]; knowledge: string[]; request: ChatStartInput; turnId?: string; assistantId: string } | null>(null);
  const durableTurnsRef = useRef(new Map<string, { turnId: string; retryable: boolean; reconciliationRequired: boolean }>());
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const personaTriggerRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollFrameRef = useRef<number | null>(null);
  // Tracks whether the user is pinned near the bottom. Default true so the
  // first render and fresh chats always pin. Flips to false if the user
  // scrolls up to read; flips back to true when they scroll back down.
  const stickToBottomRef = useRef(true);

  // -------- Draft persistence (unsent composer state) --------
  // Preserve the user's typed prompt and uploaded attachment chips across
  // navigation. Keyed by conversation scope: "new" for a blank chat, or
  // "conv:{id}" for an existing conversation.
  const draftScope = chatDraftKey(user,conversationId);
  const draftTransferRef = useRef<string | null>(null);
  const draftLoadedRef = useRef<string | null>(null);

  // -------- Workflow suggestion (chat -> job routing) --------
  const startWorkflowFn = useServerFn(startWorkflowRun);
  const suggestWorkflowFn = useServerFn(suggestWorkflow);
  const startAgenticRunFn = useServerFn(startAgenticRun);
  const [startingWorkflow, setStartingWorkflow] = useState(false);
  const templateWorkflowRequest = useRef<{signature:string;id:string} | null>(null);
  const [runFailedAsJobPendingId, setRunFailedAsJobPendingId] = useState<string | null>(null);

  const attachmentStats = useMemo(() => {
    let sizeChars = 0;
    let preview = "";
    for (const p of pending) {
      const t = p.uploaded?.extracted_text;
      if (!t) continue;
      sizeChars += t.length;
      if (preview.length < 2000) preview += `\n--- ${p.name} ---\n${t.slice(0, 800)}`;
    }
    return { count: pending.length, sizeChars, preview };
  }, [pending]);

  // Workflow-suggestion popup is disabled while sub-agents are reachable
  // directly from chat via the spawn_subagents tool.
  const WORKFLOW_SUGGESTIONS_ENABLED = false;

  const workflowSuggestion = useWorkflowSuggestion({
    prompt: input,
    attachmentCount: attachmentStats.count,
    attachmentSizeChars: attachmentStats.sizeChars,
    attachmentPreview: attachmentStats.preview,
    enabled: WORKFLOW_SUGGESTIONS_ENABLED && !isStreaming && !isStopping,
  });


  const handleRunAsJob = async () => {
    if (startingWorkflow || !workflowSuggestion.suggestion) return;
    setStartingWorkflow(true);
    try {
      const parts: string[] = [];
      if (input.trim()) parts.push(input.trim());
      for (const p of pending) {
        const t = p.uploaded?.extracted_text;
        if (t) parts.push(`--- ${p.name} ---\n${t}`);
      }
      const source_text = parts.join("\n\n").slice(0, 200_000);
      const title = input.trim().slice(0, 80) || workflowSuggestion.suggestion.name;
      const workflowInput = {
          templateSlug: workflowSuggestion.suggestion.slug,
          title,
          conversationId: conversationId ?? null,
          inputs: { source_text, user_prompt: input.trim() },
      };
      const signature = JSON.stringify(workflowInput);
      if (templateWorkflowRequest.current?.signature !== signature) templateWorkflowRequest.current = {signature,id:crypto.randomUUID()};
      const res = await startWorkflowFn({data:{...workflowInput,requestId:templateWorkflowRequest.current.id}});
      templateWorkflowRequest.current = null;
      toast.success("Job started");
      setInput("");
      setPending([]);
      try {
        if(draftScope)localStorage.removeItem(draftScope);
      } catch {
        /* ignore */
      }
      workflowSuggestion.dismiss();
      navigate({ to: "/jobs/$runId", params: { runId: res.id } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not start job");
    } finally {
      setStartingWorkflow(false);
    }
  };

  // Recover from a chat turn that hit a wall-clock / stream-close error by
  // escalating it to a background job. Pulls the failed prompt + attachment
  // text from the preceding user message, classifies via suggestWorkflow
  // (which will draft a new template on the fly if none exists), starts the
  // run, and navigates to the job page.
  const handleRunFailedAsJob = useCallback(async (assistantMessageId: string) => {
    if (runFailedAsJobPendingId) return;
    if(durableTurnsRef.current.get(assistantMessageId)?.reconciliationRequired){toast.error('This turn must be reconciled before starting another execution.');return;}
    setRunFailedAsJobPendingId(assistantMessageId);
    try {
      const list = messagesRef.current ?? [];
      const aIdx = list.findIndex((m) => m.id === assistantMessageId);
      if (aIdx <= 0) throw new Error("Couldn't find the failed message to escalate.");
      // Walk back to the most recent user turn.
      let uIdx = aIdx - 1;
      while (uIdx >= 0 && list[uIdx].role !== "user") uIdx--;
      if (uIdx < 0) throw new Error("Couldn't find the original user prompt.");
      const userMsg = list[uIdx];

      const parts: string[] = [];
      const promptText = (userMsg.content ?? "").trim();
      if (promptText) parts.push(promptText);
      for (const a of userMsg.rawAttachments ?? []) {
        if (a.extracted_text && a.extracted_text.trim()) {
          parts.push(`--- ${a.filename} ---\n${a.extracted_text}`);
        }
      }
      const source_text = parts.join("\n\n").slice(0, 200_000);
      if (!source_text) throw new Error("The original message has no text to run.");

      const started = await startAgenticRunFn({
        data: {
          prompt: source_text,
          title: promptText.slice(0, 80) || "Agentic run",
          conversationId: conversationId ?? null,
          skillIds: selectedSkillIds,
          attachments: (userMsg.rawAttachments ?? []).slice(0, 40).map((a) => ({
            filename: a.filename,
            mime_type: a.mime_type,
            storage_path: a.storage_path,
          })),
        },
      });
      toast.success("Running this agentically in the background.");
      // Keep the user in the conversation — the progress card streams status
      // here and drops the finished write-up back into the chat.
      setJobRunIds((ids) => (ids.includes(started.id) ? ids : [...ids, started.id]));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't start the agentic run");
    } finally {
      setRunFailedAsJobPendingId(null);
    }
  }, [runFailedAsJobPendingId, startAgenticRunFn, conversationId, selectedSkillIds]);

  // One-time TTL sweep on mount: drop drafts older than 14 days.
  useEffect(() => {
    try {
      const cutoff = Date.now() - 14 * 24 * 3600 * 1000;
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith("askbell:draft:")) continue;
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        try {
          const b = JSON.parse(raw);
          if (typeof b?.updatedAt === "number" && b.updatedAt < cutoff) {
            localStorage.removeItem(k);
          }
        } catch {
          localStorage.removeItem(k);
        }
      }
    } catch { /* best-effort */ }
  }, []);

  // Restore only this verified identity's draft; never adopt an unattributed legacy key.
  useEffect(() => {
    if (!draftScope) {draftLoadedRef.current=null;setInput('');setPending([]);return;}
    if(draftTransferRef.current===draftScope){draftTransferRef.current=null;draftLoadedRef.current=draftScope;return;}
    let restored:ReturnType<typeof readChatDraft>=null;
    try {restored=readChatDraft(localStorage,draftScope);} catch { /* Storage may be unavailable. */ }
    setInput(restored?.input??'');setPending((restored?.pending??[])as Pending[]);
    draftLoadedRef.current=draftScope;
  }, [draftScope]);

  // Save draft (debounced) whenever input or pending changes within the
  // currently-loaded scope. Removes the key when the composer is empty.
  useEffect(() => {
    if (!draftScope || draftLoadedRef.current !== draftScope) return;
    const key = draftScope;
    const t = setTimeout(() => {
      try {
        const persistable = pending.filter(
          (p) => (p.uploaded || p.workspaceImport) && !p.uploading && !p.error,
        );
        if (!input.trim() && persistable.length === 0) {
          localStorage.removeItem(key);
          return;
        }
        localStorage.setItem(
          key,
          JSON.stringify({ input, pending: persistable, updatedAt: Date.now() }),
        );
      } catch { /* quota or serialization — ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [draftScope, input, pending]);



  const NEAR_BOTTOM_PX = 80;

  const isNearBottom = useCallback(() => {
    const v = scrollViewportRef.current;
    if (!v) return true;
    return v.scrollHeight - v.scrollTop - v.clientHeight <= NEAR_BOTTOM_PX;
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto", force = false) => {
    if (!force && !stickToBottomRef.current) return;
    if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
    scrollFrameRef.current = requestAnimationFrame(() => {
      const viewport = scrollViewportRef.current;
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
        if (behavior === "smooth") {
          viewport.scrollTo({ top: viewport.scrollHeight, behavior: "smooth" });
        }
      }
      scrollFrameRef.current = null;
    });
  }, []);

  const resetChatScroll = useCallback((mode: "top" | "bottom") => {
    stickToBottomRef.current = true;
    if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
    scrollFrameRef.current = requestAnimationFrame(() => {
      const viewport = scrollViewportRef.current;
      if (!viewport) return;
      viewport.scrollTop = mode === "top" ? 0 : viewport.scrollHeight;
      scrollFrameRef.current = null;
    });
  }, []);

  // Track whether the user has manually scrolled away from the bottom.
  useEffect(() => {
    const v = scrollViewportRef.current;
    if (!v) return;
    const onScroll = () => {
      stickToBottomRef.current = isNearBottom();
    };
    v.addEventListener("scroll", onScroll, { passive: true });
    return () => v.removeEventListener("scroll", onScroll);
  }, [isNearBottom]);

  useLayoutEffect(() => {
    scrollToBottom("auto");
  }, [messages, scrollToBottom]);

  // When switching conversations (or starting a fresh one), clear any stale
  // scroll position from the previous chat before the next render stream lands.
  useLayoutEffect(() => {
    if (search.conversation) {
      resetChatScroll("bottom");
      return;
    }
    resetChatScroll("top");
  }, [search.conversation, resetChatScroll]);

  // When streaming ends, run a post-settle pin across a few frames so the
  // final cleanup reflow (cursor removal, composer enable, id swap) cannot
  // leave us scrolled up. Only pins if user was already near the bottom.
  useEffect(() => {
    if (isStreaming) return;
    if (!stickToBottomRef.current) return;
    let cancelled = false;
    const pin = () => {
      if (cancelled) return;
      const v = scrollViewportRef.current;
      if (v) v.scrollTop = v.scrollHeight;
    };
    pin();
    const r1 = requestAnimationFrame(() => {
      pin();
      const r2 = requestAnimationFrame(pin);
      (pin as any)._r2 = r2;
    });
    const t = window.setTimeout(pin, 120);
    return () => {
      cancelled = true;
      cancelAnimationFrame(r1);
      window.clearTimeout(t);
    };
  }, [isStreaming, messages]);

  useEffect(() => {
    const content = scrollContentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      scrollToBottom("auto");
    });

    observer.observe(content);

    return () => {
      observer.disconnect();
      if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
    };
  }, [scrollToBottom]);

  const tokensUsed = estimateTokens(messages);

  const prepareConversationRef = useRef<Promise<string | null> | null>(null);
  const prepareClientRequestIdRef = useRef<string | null>(null);
  const ensureConversation = useCallback(async (): Promise<string | null> => {
    if (conversationId) return conversationId;
    if (!user) return null;
    if (prepareConversationRef.current) return prepareConversationRef.current;
    const preparation = (async () => {
    const modelId = selectedModelId ?? (modelMode === "fast" ? "openai-direct/gpt-5.6-luna" : "openai-direct/gpt-6-astra");
    const clientRequestId = prepareClientRequestIdRef.current ?? crypto.randomUUID();
    prepareClientRequestIdRef.current = clientRequestId;
    const projectIdToAttach = pendingProjectId ?? null;
    const data = await operation<{clientRequestId:string;modelId:string;projectId?:string},{conversationId:string}>("chat_prepare", { clientRequestId, modelId, ...(projectIdToAttach?{projectId:projectIdToAttach}:{}) }, getCsrfToken());
    prepareClientRequestIdRef.current = null;
    draftTransferRef.current=chatDraftKey(user,data.conversationId);
    setConversationId(data.conversationId);
    setHistoryVersion((v) => v + 1);
    // Hydrate the project chip if we attached one
    if (projectIdToAttach) {
      const { data: proj } = await awsClient
        .from("projects").select("id, name").eq("id", projectIdToAttach).maybeSingle();
      if (proj) setCurrentProject(proj as { id: string; name: string });
    }
    // Mark this id as "just created locally" so the URL sync effect below
    // does not re-hydrate over the messages actively streaming into state.
    justCreatedConvRef.current = data.conversationId;
    // Reflect in URL for refresh-survival
    navigate({ search: { conversation: data.conversationId }, replace: true });
    return data.conversationId;
    })();prepareConversationRef.current=preparation;try{return await preparation}finally{prepareConversationRef.current=null}
  }, [conversationId, user, pendingProjectId, navigate, selectedModelId, modelMode]);

  // The assistant calls publish_output to promote already-created files to
  // deliverables; reflect that in the in-memory messages so the Outputs panel
  // updates without a reload.
  const markPublished = useCallback((paths: string[]) => {
    if (!paths.length) return;
    const set = new Set(paths);
    setMessages((prev) =>
      prev.map((m) =>
        m.generated?.length
          ? {
              ...m,
              generated: m.generated.map((g) =>
                g.storage_path && set.has(g.storage_path) ? { ...g, pinned: true } : g,
              ),
            }
          : m,
      ),
    );
  }, []);



  const lastFollowupResultRef=useRef<ChatFinalResult|null>(null);
  const retryInsights=useCallback(async()=>{
    const result=lastFollowupResultRef.current;if(!result)return;
    const token=loadTokenRef.current;setInsightLoading(true);setInsightError(null);
    try{
      await operation('chat_followups_retry',{turnId:result.turnId,kind:'insights',requestId:crypto.randomUUID()},getCsrfToken());
      if(loadTokenRef.current===token)await followupsRef.current(result,'','');
    }catch(error){if(loadTokenRef.current===token){setInsightLoading(false);setInsightError({message:'Insights could not be retried.',details:error instanceof Error?error.message:String(error)});}}
  },[]);
  // Move the current (already-saved) conversation into a project — or detach it.
  const moveConversationToProject = useCallback(
    async (projectId: string | null) => {
      if (!conversationId) return;
      const { error } = await awsClient
        .from("conversations")
        .update({ project_id: projectId })
        .eq("id", conversationId);
      if (error) {
        toast.error("Couldn't move chat.");
        return;
      }
      setPendingProjectId(projectId);
      if (projectId) {
        const { data: proj } = await awsClient
          .from("projects").select("id, name").eq("id", projectId).maybeSingle();
        setCurrentProject(proj ? (proj as { id: string; name: string }) : null);
        toast.success(`Moved to ${proj?.name ?? "project"}.`);
      } else {
        setCurrentProject(null);
        toast.success("Removed from project.");
      }
      setHistoryVersion((v) => v + 1);
    },
    [conversationId],
  );

  const handleNewChat = useCallback(() => {
    const detached=activeTurnRef.current;if(detached){detached.cancelled=true;detached.controller.abort();}activeTurnRef.current=null;streamAbortRef.current=null;setIsStreaming(false);setRetryingMessageId(null);
    justCreatedConvRef.current=null;
    lastSendRef.current=null;
    durableTurnsRef.current.clear();
    setMessages([]);
    setConversationId(null);
    setJobRunIds([]);
    loadTokenRef.current++;
    setLoadingConvId(null);
    setConversationLoadError(null);
    lastFollowupResultRef.current=null;
    setInsights([]);
    setInsightError(null);
    setPending([]);
    setWorkspaceSelectedFolderId(null);
    setWorkspaceRevealNodeId(null);
    setInput("");
    setActiveTab("chat");
    setCurrentProject(null);
    setPendingProjectId(undefined); // re-prompt picker on next send
    setSelectedSkillIds([]);
    setSelectedKnowledgeIds([]);
    setSelectedPersonaId(null);
    userTouchedPersonaRef.current = false;
    setSkillMode("auto");
    setModelMode("thinking");
    setSelectedModelId(null);
    setAutoLoadedSkillIds(new Set());
    navigate({ search: {}, replace: true });
    inputRef.current?.focus();
    // Open project picker for the fresh chat
    setPickerOpen(true);
  }, [navigate]);

  const loadConversation = useCallback(
    async (id: string) => {
      if (!user) return;
      const detached=activeTurnRef.current;
      if(detached&&detached.convId!==id){detached.cancelled=true;detached.controller.abort();activeTurnRef.current=null;streamAbortRef.current=null;setIsStreaming(false);}
      setActiveTab("chat");
      // Immediate feedback: swap the view now, hydrate underneath.
      const token = ++loadTokenRef.current;
      const stale = () => loadTokenRef.current !== token;
      setLoadingConvId(id);
      setConversationLoadError(null);
      lastFollowupResultRef.current=null;
      setConversationId(id);
      setMessages([]);
      setJobRunIds([]);
      setPending([]);
      try {
      const [{ data: msgs,error:messageError }, { data: atts,error:attachmentError }, { data: savedInsights,error:savedInsightsError }, { data: convRow,error:conversationError }, replayAuthors] = await Promise.all([
        readAllRows<any>((from,to)=>awsClient
          .from("messages")
          .select("id, role, content, created_at, error, user_id, reasoning")
          .eq("conversation_id", id)
          .order("created_at", { ascending: true }).order("id", { ascending: true }).range(from,to),()=>!stale()),
        readAllRows<any>((from,to)=>awsClient
          .from("attachments")
          .select("id, message_id, filename, mime_type, size, storage_path, extracted_text, source, pinned")
          .eq("conversation_id", id)
          .order("created_at", { ascending: true }).order("id", { ascending: true }).range(from,to),()=>!stale()),
        readAllRows<any>((from,to)=>awsClient
          .from("insights")
          .select("id, kind, text, created_at")
          .eq("conversation_id", id)
          .order("created_at", { ascending: true }).order("id", { ascending: true }).range(from,to),()=>!stale()),
        awsClient
          .from("conversations")
          .select("project_id, skill_mode, model_mode, model_id, user_id")
          .eq("id", id)
          .maybeSingle(),
        operation<{conversationId:string},{authors:{id:string;name:string}[]}>("chat_replay_authors",{conversationId:id},getCsrfToken()),
      ]);
      if (stale()) return;
      const readError=messageError??attachmentError??savedInsightsError??conversationError;
      if(readError)throw new Error(readError.message);
      if(!convRow)throw new Error('This conversation is unavailable or you no longer have access.');
      // Split attachments by source: user uploads become input chips on the
      // user message; generated artifacts become output cards on the assistant
      // message and feed the Outputs tab.
      const uploadsByMsg = new Map<string, FileCardData[]>();
      const generatedByMsg = new Map<string, FileCardData[]>();
      // Keep raw upload metadata (including extracted_text) for safe
      // model-context reconstruction. This is internal state — not rendered.
      const rawByMsg = new Map<string, {
        filename: string; mime_type: string; size: number;
        storage_path: string; extracted_text?: string | null;
      }[]>();
      await Promise.all(
        (atts ?? []).map(async (a) => {
          if (!a.message_id) return;
          const signed_url = (await getSignedUrl(a.storage_path)) ?? undefined;
          const card: FileCardData = {
            filename: a.filename,
            mime_type: a.mime_type,
            size: Number(a.size ?? 0),
            storage_path: a.storage_path,
            signed_url,
            pinned: !!(a as any).pinned,
          };
          if (a.source === "generated") {
            const list = generatedByMsg.get(a.message_id) ?? [];
            list.push(card);
            generatedByMsg.set(a.message_id, list);
          } else {
            const list = uploadsByMsg.get(a.message_id) ?? [];
            list.push(card);
            uploadsByMsg.set(a.message_id, list);
            const rawList = rawByMsg.get(a.message_id) ?? [];
            rawList.push({
              filename: a.filename,
              mime_type: a.mime_type,
              size: Number(a.size ?? 0),
              storage_path: a.storage_path,
              extracted_text: a.extracted_text ?? null,
            });
            rawByMsg.set(a.message_id, rawList);
          }
        }),
      );
      if (stale()) return;



      // Resolve display names for non-owner senders (collaborators) so we can
      // render a "shared by {name}" attribution badge on those messages.
      const ownerId = (convRow as any)?.user_id ?? null;
      const guestSenderIds = Array.from(
        new Set(
          (msgs ?? [])
            .map((m: any) => m.user_id)
            .filter((uid: string | null) => uid && ownerId && uid !== ownerId),
        ),
      ) as string[];
      const nameByUserId = new Map<string, string>();
      if (guestSenderIds.length > 0) {
        const { data: profs } = await awsClient
          .from("profiles")
          .select("user_id, display_name")
          .in("user_id", guestSenderIds);
        for (const p of profs ?? []) {
          nameByUserId.set((p as any).user_id, toSafeDisplayName((p as any).display_name));
        }
      }

      const replayAuthorByMessage = new Map(replayAuthors.authors.map(author => [author.id, author.name]));
      const hydrated: ChatMessage[] = (msgs ?? []).map((m: any) => {
        const senderId: string | null = m.user_id ?? null;
        const isGuest = !!(senderId && ownerId && senderId !== ownerId);
        return {
          id: m.id,
          role: m.role as "user" | "assistant",
          content: m.content,
          augmented_content: m.augmented_content ?? null,
          created_at: m.created_at,
          attachments: uploadsByMsg.get(m.id) ?? [],
          rawAttachments: rawByMsg.get(m.id) ?? [],
          generated: generatedByMsg.get(m.id) ?? [],
          tools: [],
          error: m.error ?? null,
          reasoning: m.reasoning ?? null,
          sharedByName: isGuest ? (nameByUserId.get(senderId!) ?? "Collaborator") : null,
          sourceAuthorName: replayAuthorByMessage.get(m.id) ?? null,
        };
      });

      const { turns: turnRows } = await readChatTurns(id,()=>!stale());
      if(stale())return;
      const turns = new Map<string, { turnId: string; retryable: boolean; reconciliationRequired: boolean }>();
      for (const turn of turnRows) if (turn.assistantMessageId && !turns.has(turn.assistantMessageId)) turns.set(turn.assistantMessageId, turn);
      durableTurnsRef.current = turns;
      const withTools=await restoreChatTools(recoverChatMessages(hydrated,turnRows),id,()=>!stale());
      if(stale())return;
      setMessages(withTools);
      setConversationId(id);
      // Restore live cards for jobs this conversation started.
      // Dismissal lives on the run (dismissed_at) so it sticks across reloads and devices.
      readAllRows<{id:string;status:string;created_at:string}>((from,to)=>awsClient
        .from("workflow_runs")
        .select("id, status, created_at")
        .eq("conversation_id", id)
        .is("parent_run_id", null)
        .is("dismissed_at", null)
        .order("created_at", { ascending: true }).order("id")
        .range(from,to),()=>!stale())
        .then(({ data: runs,error }) => {
          if(stale())return;
          if(error){toast.error('Conversation jobs could not be loaded.',{description:error.message});return;}
          setJobRunIds(((runs ?? []) as any[]).map((r) => r.id));
        });

      // Hydrate project chip if this conversation belongs to one
      const projId = (convRow as any)?.project_id ?? null;
      if (projId) {
        const { data: proj,error } = await awsClient.from("projects").select("id, name").eq("id", projId).maybeSingle();
        if(stale())return;
        if(error)throw new Error(error.message);
        setCurrentProject(proj ? (proj as { id: string; name: string }) : null);
        setPendingProjectId(projId);
      } else {
        setCurrentProject(null);
        setPendingProjectId(null);
      }
      const restored: Insight[] = (savedInsights ?? []).map((r: any) => ({
        id: r.id,
        kind: r.kind as Insight["kind"],
        text: r.text,
        created_at: r.created_at,
      }));
      setInsights(restored);
      setInsightError(null);
      // Hydrate skill, knowledge, and persona selection for this conversation
      const [{ data: skillRows,error:skillError }, { data: knowledgeRows,error:knowledgeError }, { data: personaRows,error:personaError }] = await Promise.all([
        awsClient
          .from("conversation_skills")
          .select("skill_id")
          .eq("conversation_id", id),
        awsClient
          .from("conversation_knowledge")
          .select("knowledge_id")
          .eq("conversation_id", id),
        awsClient
          .from("conversation_personas")
          .select("persona_id")
          .eq("conversation_id", id)
          .maybeSingle(),
      ]);
      if (stale()) return;
      const selectionError=skillError??knowledgeError??personaError;
      if(selectionError)throw new Error(selectionError.message);
      setSelectedSkillIds((skillRows ?? []).map((r: any) => r.skill_id));
      setSelectedKnowledgeIds((knowledgeRows ?? []).map((r: any) => r.knowledge_id));
      setSelectedPersonaId((personaRows as any)?.persona_id ?? null);
      userTouchedPersonaRef.current = true; // existing chats keep what was saved; don't auto-overwrite
      const mode = (convRow as any)?.skill_mode;
      setSkillMode(mode === "manual" ? "manual" : "auto");
      const mMode = (convRow as any)?.model_mode;
      setModelMode(mMode === "thinking" ? "thinking" : "fast");
      const mId = (convRow as any)?.model_id;
      setSelectedModelId(typeof mId === "string" && mId ? mId : null);
      setAutoLoadedSkillIds(new Set());
      const completed=turnRows.find(turn=>turn.canManage===true&&turn.status==='succeeded');
      if(completed){const original=hydrated.find(message=>message.id===completed.userMessageId);void followupsRef.current({turnId:completed.turnId,conversationId:id,assistantMessageId:completed.assistantMessageId},original?.content??'',original?.content??'');}
      const running=turnRows.find(turn=>turn.canManage===true&&['queued','running','waiting'].includes(turn.status));
      if(running&&!activeTurnRef.current){
        const original=hydrated.find(message=>message.id===running.userMessageId);
        if(!original)throw new Error('The active turn has no saved user message.');
        const uploads:Pending[]=(atts??[]).filter(a=>a.message_id===running.userMessageId&&a.source!=='generated').map(a=>({id:a.id,name:a.filename,size:Number(a.size),type:a.mime_type,uploaded:{id:a.id,filename:a.filename,size:Number(a.size),mime_type:a.mime_type,storage_path:a.storage_path,extracted_text:a.extracted_text??undefined}}));
        const turn:ActiveTurn={id:crypto.randomUUID(),clientRequestId:running.turnId,serverTurnId:running.turnId,kind:running.isRetry?'retry':'send',phase:'streaming',controller:new AbortController(),cancelled:false,convId:id,userMsgId:running.userMessageId,userMessageRowId:running.userMessageId,assistantId:running.assistantMessageId,originalInput:original.content,originalPending:uploads};
        activeTurnRef.current=turn;streamAbortRef.current=turn.controller;setIsStreaming(true);
        setMessages(previous=>previous.some(message=>message.id===turn.assistantId)?previous:[...previous,{id:turn.assistantId,role:'assistant',content:'',created_at:original.created_at,tools:[]}]);
        const owns=()=>!stale()&&!turn.cancelled&&activeTurnRef.current?.id===turn.id;
        void streamChatTurn({
          body:{clientRequestId:running.turnId,prompt:original.content,attachmentIds:[],modelId:String(mId??'')},
          resume:{turnId:running.turnId,conversationId:id,messageId:running.userMessageId},signal:turn.controller.signal,
          onEvent:event=>{
            if(!owns())return;
            if(event.type==='error')durableTurnsRef.current.set(turn.assistantId,{turnId:running.turnId,retryable:event.reviewRequired!==true&&event.status!=='cancelled',reconciliationRequired:event.reviewRequired===true});
            if(['skills_loaded','skill_loaded'].includes(String(event.type))&&Array.isArray(event.skillIds))setAutoLoadedSkillIds(new Set(event.skillIds));
            setMessages(previous=>previous.map(message=>message.id!==turn.assistantId?message:event.type==='error'?{...message,error:{headline:event.message??'The response could not be completed.',retryable:event.reviewRequired!==true&&event.status!=='cancelled',recoverableAsJob:event.reviewRequired!==true}}:applyChatProgress(message,event)));
          },
          onFinal:async()=>{if(owns())await loadConversation(id);},
        }).catch(error=>{if(owns())toast.error('Could not reconnect to the saved turn.',{description:error instanceof Error?error.message:String(error)});})
          .finally(()=>{if(activeTurnRef.current?.id===turn.id){activeTurnRef.current=null;streamAbortRef.current=null;setIsStreaming(false);}});
      }
      } catch(error) {
        if(!stale())setConversationLoadError(error instanceof Error?error.message:'The conversation could not be loaded.');
      } finally {
        if (!stale()) setLoadingConvId(null);
      }
    },
    [user],
  );

  // Sync URL ?conversation=… into loaded state. Runs on mount and whenever the
  // search param changes (e.g. clicking a chat in HistoryPanel).
  useEffect(() => {
    if (!user) return;
    const target = search.conversation ?? null;
    if (target && target !== conversationId) {
      // Skip loadConversation when the id is one we just created locally —
      // otherwise it stomps the actively-streaming messages with the (mostly
      // empty) DB view, and the Stop button appears to do nothing.
      if (justCreatedConvRef.current === target) {
        justCreatedConvRef.current = null;
        setConversationId(target);
      } else {
        loadConversation(target);
      }
    } else if (!target && conversationId) {
      // Sidebar New chat and deletion clear the route without remounting this
      // page. Detach the old turn and invalidate pending loads as well as
      // clearing its transcript, attachments, and selected context.
      handleNewChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.conversation, user]);

  // Consume ?prefill= from external entry points (e.g. Feed → "Ask AskBell about this").
  // Seed the composer once, focus it, then strip the param from the URL.
  useEffect(() => {
    const text = search.prefill;
    if (!text) return;
    setInput((prev) => (prev && prev.trim().length > 0 ? prev : text));
    requestAnimationFrame(() => inputRef.current?.focus());
    navigate({
      to: "/chat",
      search: search.conversation ? { conversation: search.conversation } : {},
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.prefill]);

  const handleFiles = useCallback(async (files: File[], addToWorkspace = false) => {
    if (!user) { toast.error("Please sign in to upload files."); return; }
    if(loadingConvId||conversationLoadError){toast.error('Finish opening the conversation before attaching files.');return;}

    const tooBig = files.filter((f) => f.size > MAX_FILE_BYTES);
    if (tooBig.length) {
      toast.error(`${tooBig.length === 1 ? "1 file is" : `${tooBig.length} files are`} larger than ${Math.round(MAX_FILE_BYTES / (1024 * 1024))} MB and were skipped.`);
    }
    let accepted = files.filter((f) => f.size <= MAX_FILE_BYTES);

    const remainingSlots = Math.max(0, MAX_FILES_PER_MESSAGE - pending.length);
    if (accepted.length > remainingSlots) {
      toast.error(`Max ${MAX_FILES_PER_MESSAGE} attachments per message — ${accepted.length - remainingSlots} skipped.`);
      accepted = accepted.slice(0, remainingSlots);
    }
    if (!accepted.length) return;

    const uploadConversationId = conversationId ?? await ensureConversation();
    if (!uploadConversationId) { toast.error("A conversation is required before uploading a chat file."); return; }
    const workspace = addToWorkspace ? await workspaceApi.get(uploadConversationId) : null;
    const selectedFolder = workspace?.nodes.find((node) => node.id === workspaceSelectedFolderId && node.kind === "directory" && !node.trashedAt) ?? null;
    if (addToWorkspace && workspaceSelectedFolderId && !selectedFolder) setWorkspaceSelectedFolderId(null);

    const newItems: Pending[] = accepted.map((f) => ({
      id: crypto.randomUUID(), name: f.name, size: f.size, type: f.type, uploading: true,
    }));
    setPending((p) => [...p, ...newItems]);

    const completed = await Promise.all(accepted.map(async (f, i) => {
      const item = newItems[i];
      try {
        const up = await uploadChatFile(f, user.id, uploadConversationId);
        if (!addToWorkspace) {
          setPending((p) => p.map((x) => x.id === item.id ? {
            ...x,
            uploading: false,
            uploaded: up,
            extractionError: up.extraction_attempted && up.extraction_error ? up.extraction_error : undefined,
          } : x));
        }
        if (up.extraction_attempted && up.extraction_error) {
          toast.warning(`Couldn't read "${f.name}". Retry extraction or remove the file before sending.`);
        }
        return { item, file: f, uploaded: up };
      } catch (e: any) {
        console.error(e);
        setPending((p) => p.map((x) => x.id === item.id ? { ...x, uploading: false, error: e?.message ?? "Upload failed" } : x));
        toast.error(`Upload failed: ${f.name}`);
        return null;
      }
    }));

    const verified = completed.filter((entry): entry is NonNullable<typeof entry> => Boolean(entry?.uploaded.id));
    if (!workspace || !verified.length) return;
    try {
      const committed = await workspaceApi.commitUploads({
        chatId: uploadConversationId,
        parentId: selectedFolder?.id,
        items: verified.map((entry) => ({ attachmentId: entry.uploaded.id!, relativePath: entry.file.name })),
        expectedWorkspaceRevision: workspace.revision,
        idempotencyKey: crypto.randomUUID(),
      });
      setPending((items) => items.map((item) => {
        const complete = verified.find((entry) => entry.item.id === item.id);
        return complete ? { ...item, uploading: false, uploaded: complete.uploaded, extractionError: complete.uploaded.extraction_attempted && complete.uploaded.extraction_error ? complete.uploaded.extraction_error : undefined } : item;
      }));
      setWorkspaceRevealNodeId(committed.rootNodeIds[0] ?? committed.createdNodeIds[0] ?? null);
      setWorkspaceRefreshKey((key) => key + 1);
      toast.success(`${verified.length === 1 ? verified[0]!.file.name : `${verified.length} files`} added to ${selectedFolder?.name ?? "Workspace Files"}.`);
    } catch (cause) {
      await Promise.all(verified.map((entry) => awsClient.storage.from("chat-uploads").remove([entry.uploaded.id!])))
        .catch(() => undefined);
      const message = cause instanceof Error ? cause.message : "The dropped files could not be added to Workspace Files.";
      const failedIds = new Set(verified.map((entry) => entry.item.id));
      setPending((items) => items.map((item) => failedIds.has(item.id) ? { ...item, uploading: false, error: message, uploaded: undefined } : item));
      toast.error(message);
    }
  }, [user, conversationId, ensureConversation, pending.length, loadingConvId, conversationLoadError, workspaceSelectedFolderId]);

  const handleFolderStructure = useCallback(async (tree: DroppedWorkspaceTree) => {
    if (!user) { toast.error("Please sign in to upload folders."); return; }
    if (loadingConvId || conversationLoadError) { toast.error("Finish opening the conversation before attaching folders."); return; }
    const totalBytes = tree.files.reduce((sum, item) => sum + item.file.size, 0);
    const oversized = tree.files.find((item) => item.file.size > MAX_FILE_BYTES);
    if (oversized) { toast.error(`The folder was not added because "${oversized.file.name}" is larger than ${Math.round(MAX_FILE_BYTES / (1024 * 1024))} MB.`); return; }

    const chipId = crypto.randomUUID();
    const chip: Pending = {
      id: chipId,
      name: "Folders+",
      size: totalBytes,
      type: "application/x-directory",
      kind: "folder",
      uploading: true,
    };
    setPending((items) => [...items, chip]);
    const uploaded: UploadedAttachment[] = [];
    try {
      const chatId = conversationId ?? await ensureConversation();
      if (!chatId) throw new Error("A conversation is required before uploading folders.");
      const workspace = await workspaceApi.get(chatId);
      const selectedFolder = workspace.nodes.find((node) => node.id === workspaceSelectedFolderId && node.kind === "directory" && !node.trashedAt) ?? null;
      if (workspaceSelectedFolderId && !selectedFolder) setWorkspaceSelectedFolderId(null);
      const activeDirectories = workspace.nodes.filter((node) => node.kind === "directory" && !node.trashedAt).length;
      if (tree.files.length > workspace.quota.fileLimit - workspace.quota.files - workspace.quota.reservedFiles) throw new Error(`The complete folder cannot fit: this chat supports ${workspace.quota.fileLimit} current files.`);
      if (totalBytes > workspace.quota.byteLimit - workspace.quota.bytes - workspace.quota.reservedBytes) throw new Error("The complete folder cannot fit in this chat's Workspace Files storage.");
      if (activeDirectories + tree.directories.length > 1_000) throw new Error("The complete folder cannot fit: a chat supports at most 1,000 folders.");

      for (const item of tree.files) {
        uploaded.push(await uploadChatFile(item.file, user.id, chatId, undefined, { extractContent: false }));
      }
      if (uploaded.some((item) => !item.id)) throw new Error("A verified folder upload did not return every file identity.");
      const committed = await workspaceApi.commitUploads({
        chatId,
        parentId: selectedFolder?.id,
        items: uploaded.map((item, index) => ({ attachmentId: item.id!, relativePath: tree.files[index]!.relativePath })),
        directories: tree.directories,
        expectedWorkspaceRevision: workspace.revision,
        idempotencyKey: crypto.randomUUID(),
      });
      if (!committed.rootNodeIds.length) throw new Error("The folder structure was saved without a removable workspace root.");
      setPending((items) => items.map((item) => item.id === chipId ? {
        ...item,
        uploading: false,
        workspaceImport: { chatId, rootNodeIds: committed.rootNodeIds, workspaceRevision: committed.workspaceRevision },
      } : item));
      setWorkspaceRefreshKey((key) => key + 1);
      setWorkspaceRevealNodeId(committed.rootNodeIds[0] ?? committed.createdNodeIds[0] ?? null);
      toast.success(`Folder structure added to ${selectedFolder?.name ?? "Workspace Files"}.`);
    } catch (cause) {
      await Promise.all(uploaded.filter((item) => item.id).map((item) => awsClient.storage.from("chat-uploads").remove([item.id!])))
        .catch(() => undefined);
      const message = cause instanceof Error ? cause.message : "The folder structure could not be added.";
      setPending((items) => items.map((item) => item.id === chipId ? { ...item, uploading: false, error: message } : item));
      toast.error(message);
    }
  }, [user, loadingConvId, conversationLoadError, conversationId, ensureConversation, workspaceSelectedFolderId]);

  const removePending = useCallback(async (id: string) => {
    const item = pending.find((entry) => entry.id === id);
    if (!item || item.uploading) return;
    if (!item.workspaceImport) { setPending((items) => items.filter((entry) => entry.id !== id)); return; }
    setPending((items) => items.map((entry) => entry.id === id ? { ...entry, uploading: true, error: undefined } : entry));
    try {
      const workspace = await workspaceApi.get(item.workspaceImport.chatId);
      await workspaceApi.removeImport({
        chatId: item.workspaceImport.chatId,
        nodeIds: item.workspaceImport.rootNodeIds,
        expectedWorkspaceRevision: workspace.revision,
        idempotencyKey: crypto.randomUUID(),
      });
      setPending((items) => items.filter((entry) => entry.id !== id));
      setWorkspaceRefreshKey((key) => key + 1);
      toast.success("Folder structure removed from Workspace Files.");
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "The folder structure could not be removed.";
      setPending((items) => items.map((entry) => entry.id === id ? { ...entry, uploading: false, error: message } : entry));
      toast.error(message);
    }
  }, [pending]);

  const handleRetryExtraction = useCallback(async (id: string) => {
    const item = pending.find((p) => p.id === id);
    if (!item?.uploaded) return;
    setPending((p) => p.map((x) => x.id === id ? { ...x, retrying: true } : x));
    const updated = await retryExtraction(item.uploaded);
    setPending((p) => p.map((x) => x.id === id ? {
      ...x,
      retrying: false,
      uploaded: updated,
      extractionError: updated.extraction_error ?? undefined,
    } : x));
    if (!updated.extraction_error) toast.success(`Read "${item.name}" successfully.`);
  }, [pending]);

  const dragDepth = useRef(0);
  const handleDragEnter = (e: DragEvent) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  };
  const handleDragOver = (e: DragEvent) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };
  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    try {
      const tree = await readDroppedWorkspaceTree(e.dataTransfer);
      if (tree.hasDirectories) await handleFolderStructure(tree);
      else if (tree.files.length) await handleFiles(tree.files.map((item) => item.file), true);
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : "The dropped folder could not be read.");
    }
  };

  const runCompletedFollowups = useCallback(async (result: ChatFinalResult, userText: string, _memoryText: string) => {
    const token=loadTokenRef.current,key=`${token}:${result.turnId}`;
    if(followupObserversRef.current.has(key))return;
    followupObserversRef.current.add(key);
    const current=()=>loadTokenRef.current===token;
    lastFollowupResultRef.current=result;setInsightLoading(true);setInsightError(null);
    try {
      await observeChatFollowups(()=>operation<{turnId:string},{entries:FollowupEntry[]}>("chat_followups_state",{turnId:result.turnId},getCsrfToken()),entry=>{
        if(entry.status==='unavailable')return;
        if(entry.status!=='succeeded'){
          if(entry.kind==='insights')setInsightError({message:"The reply was saved, but insights could not be completed.",details:entry.error??'This follow-up requires review.'});
          else toast.error(`The reply was saved, but ${entry.kind==='metadata'?'its title':'memory extraction'} could not be completed.`,{description:entry.error??undefined});
          return;
        }
        if(entry.kind==='metadata'){window.dispatchEvent(new Event(CONVERSATIONS_CHANGED_EVENT));setHistoryVersion(v=>v+1);}
        if(entry.kind==='insights'){
          setInsightError(null);
          const fresh=(entry.result?.insights??[])as Insight[];
          setInsights(previous=>{const byId=new Map(previous.map(item=>[item.id,item]));for(const item of fresh)byId.set(item.id,item);return[...byId.values()];});
          setInsightLoading(false);
        }
        if(entry.kind==='memory')for(const memory of entry.result?.memories??[]){
          if(!memory.id||!memory.text||notifiedMemoriesRef.current.has(memory.id))continue;
          notifiedMemoriesRef.current.add(memory.id);
          toast(`Remembered: ${memory.text}`,{duration:8000,action:{label:'Undo',onClick:async()=>{const {error}=await awsClient.from('user_memories').delete().eq('id',memory.id);if(error)toast.error("Couldn't undo.");else toast.success('Forgotten.');}},cancel:{label:'Manage',onClick:()=>navigate({to:'/settings/memory'as any})}});
        }
      },current);
    } catch(error) {
      if(current())setInsightError({message:'Conversation follow-ups could not be checked.',details:error instanceof Error?error.message:String(error)});
    } finally {followupObserversRef.current.delete(key);if(current())setInsightLoading(false);}
  }, [navigate]);
  followupsRef.current=runCompletedFollowups;
  const handleSend = async (e?: FormEvent) => {
    e?.preventDefault();
    if(readOnlyChat)return;
    if(loadingConvId||conversationLoadError){toast.error('Finish opening the conversation before sending.');return;}
    const text = input.trim();
    const ready = pending.filter((p) => p.uploaded);
    const readyFolders = pending.filter((p) => p.workspaceImport && !p.uploading && !p.error);
    // This ref is the synchronous lock; React state does not update quickly
    // enough to prevent two submit events in the same tick.
    if ((!text && !ready.length && !readyFolders.length) || activeTurnRef.current) return;
    if (pending.some((p) => p.uploading)) { toast.info("Files still uploading…"); return; }
    const failedUpload = pending.find((item) => item.error || (item.kind !== "folder" && !item.uploaded?.id));
    if (failedUpload) { toast.error(`Upload "${failedUpload.name}" successfully or remove it before sending.`); return; }
    const imageFiles = ready.filter((item) => item.uploaded!.mime_type.startsWith("image/"));
    if (imageFiles.some((item) => !["image/png", "image/jpeg", "image/webp"].includes(item.uploaded!.mime_type))) { toast.error("Chat images must be PNG, JPEG, or WebP."); return; }
    if (imageFiles.length > 20 || imageFiles.some((item) => item.uploaded!.size > 250 * 1024 * 1024)) { toast.error("Use up to twenty images, 250 MiB each."); return; }
    const unreadable = ready.find((item) => !item.uploaded!.mime_type.startsWith("image/") && !item.uploaded!.extracted_text?.trim());
    if (unreadable) { toast.error(`Read "${unreadable.name}" successfully or remove it before sending.`); return; }
    if (!user) { toast.error("Please sign in to chat."); return; }
    setClearFinishedJobsToken((t) => t + 1);

    const attachments: FileCardData[] = ready.map((r) => ({
      filename: r.uploaded!.filename,
      mime_type: r.uploaded!.mime_type,
      size: r.uploaded!.size,
      storage_path: r.uploaded!.storage_path,
      signed_url: r.uploaded!.signed_url,
    }));

    // If extracted text is small, inline it directly. If it's large, skip inlining
    // (which can blow the model's context and cause silent edge-function timeouts)
    // and instead point the model at the sandbox-mounted file so it uses run_python.
    const INLINE_TEXT_LIMIT = 20_000; // chars
    const safeName = (s: string) => s.replace(/[^a-zA-Z0-9._-]/g, "_");

    const contentParts: any[] = [];
    const userText = text || (readyFolders.length > 0 ? "Use the folder structure added to Workspace Files." : ready.length > 0 ? "Infer the user intent from the file contents" : "(see attached files)");
    let augmented = userText;
    for (const r of ready) {
      const u = r.uploaded!;
      if (u.mime_type.startsWith("image/")) continue; // images go as image_url below
      if (u.extracted_text && u.extracted_text.length <= INLINE_TEXT_LIMIT) {
        augmented += `\n\n--- Attached: ${u.filename} ---\n${u.extracted_text}\n--- end ${u.filename} ---`;
      } else {
        // Large or unreadable: tell the model the file is mounted in the sandbox.
        const path = `/home/user/inputs/${safeName(u.filename)}`;
        const preview = u.extracted_text
          ? `\nFirst ${Math.min(500, u.extracted_text.length)} chars (preview only):\n${u.extracted_text.slice(0, 500)}…`
          : "";
        const sizeKb = Math.round(u.size / 1024);
        augmented += `\n\n<attached_file>\nfilename: ${u.filename}\nmime_type: ${u.mime_type}\nsize: ${sizeKb} KB\nsandbox_path: ${path}\nThe full file content is too large to include inline. To work with this file, call run_python and read it from "${path}" (e.g. with pandas: pd.read_excel("${path}") or pd.read_csv("${path}")). Do NOT ask the user to re-paste the data.${preview}\n</attached_file>`;
      }
    }
    contentParts.push({ type: "text", text: augmented });
    for (const r of ready) {
      const u = r.uploaded!;
      if (u.mime_type.startsWith("image/") && u.signed_url) {
        contentParts.push({ type: "image_url", image_url: { url: u.signed_url } });
      }
    }

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(), role: "user", content: userText,
      augmented_content: augmented !== userText ? augmented : null,
      created_at: new Date().toISOString(), attachments,
      rawAttachments: ready.map((r) => ({
        filename: r.uploaded!.filename,
        mime_type: r.uploaded!.mime_type,
        size: r.uploaded!.size,
        storage_path: r.uploaded!.storage_path,
        extracted_text: r.uploaded!.extracted_text ?? null,
      })),
      // If the current user is a collaborator (not the conversation owner),
      // tag this locally-appended message so the attribution badge renders
      // immediately, matching what the owner will see on reload.
      sharedByName: chatRole === "collaborator"
        ? toSafeDisplayName((user?.user_metadata as any)?.display_name, user?.email?.split("@")[0] || "Collaborator")
        : null,
    };
    setMessages((prev) => [...prev, userMsg]);
    // Snapshot for Stop: restore original prompt text + already-uploaded
    // attachments back into the composer if the user aborts this turn.
    const originalInput = text;
    const originalPending: Pending[] = ready;
    const turn: ActiveTurn = {
      id: crypto.randomUUID(),
      clientRequestId: crypto.randomUUID(),
      kind: "send",
      phase: "preparing",
      controller: new AbortController(),
      cancelled: false,
      userMsgId: userMsg.id,
      assistantId: "",
      originalInput,
      originalPending,
      convId: null,
      userMessageRowId: null,
    };
    activeTurnRef.current = turn;
    streamAbortRef.current = turn.controller;
    setInput(""); setPending([]); setIsStreaming(true);
    // Clear any persisted draft for this scope — the user submitted, so
    // there's nothing left to restore on next visit.
    try {
      if(draftScope)localStorage.removeItem(draftScope);
      // A send in an existing conversation must preserve the separate new-chat draft.
      if (!conversationId) {
        const newDraftKey = chatDraftKey(user, null);
        if (newDraftKey) localStorage.removeItem(newDraftKey);
      }
    } catch { /* best-effort */ }
    // Always pin to bottom on submit so the user can see their message land
    // and the assistant response begin streaming, even if they had scrolled up.
    stickToBottomRef.current = true;
    scrollToBottom("smooth", true);

    // Persist user message + ensure conversation

    let convId: string | null;
    try {
      convId = await ensureConversation();
    } catch (error) {
      if (activeTurnRef.current?.id === turn.id) {
        activeTurnRef.current = null;
        streamAbortRef.current = null;
        setIsStreaming(false);
        setInput(originalInput);
        setPending(originalPending);
        setMessages(previous => previous.filter(message => message.id !== userMsg.id));
        toast.error(error instanceof Error ? error.message : 'The conversation could not be prepared.');
      }
      return;
    }
    turn.convId = convId;
    if (turn.cancelled) return;
    // chat_start commits the initial selections, message and job together.
    if (turn.cancelled) return;

    const assistantId = crypto.randomUUID();
    setMessages((prev) => [...prev, {
      id: assistantId, role: "assistant", content: "",
      created_at: new Date().toISOString(), tools: [], generated: [],
    }]);
    turn.assistantId = assistantId;
    // Force-scroll again now that the assistant placeholder exists, so the
    // streaming indicator is visible at the bottom of the viewport.
    scrollToBottom("auto", true);

    const updateAssistant = (fn: (m: ChatMessage) => ChatMessage) => {
      setMessages((prev) => prev.map((m) => m.id === assistantId ? fn(m) : m));
    };

    let finalAssistantText = "";
    let finalReasoning = "";
    let reasoningStartedAt: number | null = null;
    let finalReasoningDurationMs: number | null = null;
    // Track stream completeness so we can detect silent terminations
    // (worker shutdown / proxy timeout / dropped connection mid-stream)
    // AND empty successful completions (server sent `done` but no text/tools).
    let sawDone = false;
    let sawError = false;
    let sawAnyEvent = false;
    let sawToolActivity = false;
    let generatedFileCount = 0;
    let durableFinalLoaded = false;

    try {
      if (turn.cancelled) return;
      // Build prior history with safe context reconstruction:
      // small attachments inline, large ones become compact <attached_file>
      // hints. This prevents legacy giant `augmented_content` from being
      // replayed verbatim and crashing follow-up turns.
      const apiMessages = [
        ...messagesRef.current.map((m) => ({
          role: m.role,
          content: m.role === "user" ? buildHistoricalUserContent(m) : m.content,
        })),
        { role: "user", content: contentParts.length === 1 ? augmented : contentParts },
      ];

      // Send selections; the backend authorizes and snapshots their content.
      const attachmentIds = ready.map((item) => item.uploaded?.id).filter((id): id is string => !!id);
      const durableRequest: ChatStartInput = {
        conversationId: convId ?? undefined,
        clientRequestId: turn.clientRequestId,
        prompt: text,
        attachmentIds,
        modelId: selectedModelId ?? (modelMode === "fast" ? "openai-direct/gpt-5.6-luna" : "openai-direct/gpt-6-astra"),
        ...(selectedSkillIds.length ? { skillIds: selectedSkillIds } : {}),
        ...(selectedKnowledgeIds.length ? { knowledgeIds: selectedKnowledgeIds } : {}),
        ...(selectedPersonaId ? { personaId: selectedPersonaId } : userTouchedPersonaRef.current ? { personaId: null } : {}),
        skillMode,
      };
      lastSendRef.current = { contentParts, userText, convId, apiMessages, knowledge: [], request: structuredClone(durableRequest), assistantId };

      turn.phase = "streaming";
      const streamOutcome = await streamChatTurn({
        body: durableRequest,
        signal: turn.controller.signal,
        cancelRequested: () => turn.stopRequested === true,
        onTurnId: (id) => { turn.serverTurnId = id; if(lastSendRef.current?.assistantId===assistantId)lastSendRef.current.turnId=id; },
        onStarted: async (started) => {
          if(turn.cancelled)return;
          setAutoLoadedSkillIds(new Set(started.loadedSkillIds ?? []));
          turn.userMessageRowId = started.messageId || null;
        },
        onFinal: async (result) => {
          if(turn.cancelled)return;
          durableFinalLoaded = true;
          if (convId) await loadConversation(convId);
          if (!turn.cancelled) void runCompletedFollowups(result, userText, text);
        },
        onEvent: (ev) => {
          if (turn.cancelled) return;
          sawAnyEvent = true;
          if (['model_start','model_end','delta','reasoning_delta','reasoning_start','reasoning','reasoning_end','tool_start','tool_progress','tool_result'].includes(String(ev.type))) updateAssistant(message=>applyChatProgress(message,ev));
          if (["skills_loaded","skill_loaded"].includes(String(ev.type)) && Array.isArray(ev.skillIds)) {
            setAutoLoadedSkillIds(new Set(ev.skillIds));
          } else if (ev.type === "done") {
            sawDone = true;
          } else if (ev.type === "error") {
            sawError = true;
            const headline = typeof ev.message === "string" ? ev.message : "The model couldn't finish responding.";
            updateAssistant((message) => ({ ...message, error: { headline, details: typeof ev.details === "string" ? ev.details : null, retryable: ev.status !== "cancelled" && ev.reviewRequired !== true, recoverableAsJob: ev.reviewRequired !== true } }));
            if (ev.status !== "cancelled") toast.error(headline);
          }
        },
      });
      sawDone = sawDone || streamOutcome.sawDone;
      sawError = sawError || streamOutcome.sawError;
      sawAnyEvent = sawAnyEvent || streamOutcome.sawAnyEvent;

      // Silent-end detection: stream closed cleanly but the server never sent
      // a `done` event and never sent an explicit `error`. This happens when
      // the edge function gets killed mid-stream (CPU/wall-time limit, worker
      // shutdown, proxy timeout). Surface it as a retryable error block so
      // the user isn't left staring at an empty bubble.
      if (turn.cancelled) {
        // User aborted — handleStop already restored the composer and
        // removed the placeholder messages. Skip all error surfacing.
      } else if (!sawDone && !sawError) {
        const headline = finalAssistantText.trim()
          ? "The response was cut off."
          : "The response was cut off before it started.";
        const details = sawAnyEvent
          ? "The server closed the stream without finishing. This usually means the request hit a processing limit — most often a large volume of files being prepared for the sandbox, or a very long run. Retry: file mounting is cached per conversation, so a second attempt is normally much lighter. You can also run it as a job below."
          : "The server closed the connection before sending any data. The function may have crashed during startup. Please retry.";
        updateAssistant((m) => {
          // Also fail any tools that were still 'running' when the stream died.
          const tools = (m.tools ?? []).map((t) =>
            t.status === "running" ? { ...t, status: "error" as const } : t
          );
          return { ...m, tools, error: { headline, details, retryable: true, recoverableAsJob: true } };
        });
        toast.error(headline);
      }
    } catch (err: any) {
      if (turn.cancelled || err?.name === "AbortError") {
        // Clean user-initiated cancel — do nothing here; handleStop owns UI.
      } else {
        const msg = err?.message || "Something went wrong.";
        updateAssistant((m) => ({ ...m, error: { headline: chatFailureHeadline(Boolean(turn.serverTurnId)), details: msg, retryable: true } }));
        toast.error(msg);
      }
    } finally {
      const stillOwner = activeTurnRef.current?.id === turn.id;
      if (stillOwner) {
        activeTurnRef.current = null;
        streamAbortRef.current = null;
        setIsStreaming(false);
      }

    }
  };

  // Abort the in-flight chat stream, discard partial assistant output,
  // remove the just-sent user message, and restore the composer so the
  // user can adjust the prompt and resend. Also deletes the persisted
  // user-message row (if any) so a reload doesn't resurrect it.
  const handleStop = useCallback(() => {
    // 1. Immediate visual acknowledgement — flips the button state right now,
    //    before any other work. Cleared after a short flash.
    setIsStopping(true);
    setTimeout(() => setIsStopping(false), 450);

    const ctx = activeTurnRef.current;

    // 2. Detach the UI from the stream. Mark cancelled, abort the fetch, and
    //    cancel the reader if present. Fire-and-forget — do not await.
    if (ctx) {
      ctx.cancelled = true;
      ctx.stopRequested = true;
      ctx.phase = "cleaning";
    }
    try { streamAbortRef.current?.abort(); } catch { /* noop */ }
    try { ctx?.controller.abort(); } catch { /* noop */ }
    // Explicit Stop: tell the server so it cancels the turn (and any model run
    // still going in the background) instead of parking it for resumption.
    if (ctx?.serverTurnId) void cancelChatTurn(ctx.serverTurnId).catch(() => toast.error("The server could not confirm cancellation. Reopen this conversation to check the turn."));


    // 3. Synchronously return the UI to idle. The backend fetch may still be
    //    running in the background; we don't care — every UI-mutating site
    //    inside handleSend/handleRetryMessage is guarded by `turn.cancelled`
    //    (or by `stillOwner` in the finally block), so nothing it emits from
    //    here on can touch the visible state.
    activeTurnRef.current = null;
    streamAbortRef.current = null;
    setIsStreaming(false);
    setRetryingMessageId(null);

    if (!ctx) {
      // Hard-reset fallback: button showed Stop but we have no turn context.
      // Drop any empty assistant placeholder that might be lingering.
      setMessages((prev) => prev.filter((m) => !(m.role === "assistant" && !m.content && !(m.tools?.length) && !(m.generated?.length))));
      return;
    }

    if (ctx.kind === "send") {
      // Remove the user + assistant placeholder from the transcript.
      setMessages((prev) => prev.filter((m) => m.id !== ctx.assistantId && m.id !== ctx.userMsgId));
      // Restore composer so the user can edit and resend.
      if (typeof ctx.originalInput === "string") setInput(ctx.originalInput);
      if (ctx.originalPending?.length) setPending(ctx.originalPending);
      // Refocus the textarea for immediate editing.
      setTimeout(() => { try { inputRef.current?.focus(); } catch { /* noop */ } }, 0);
      // The cancellation endpoint unlinks uploads and removes this submitted
      // user message. Completed generated artifacts remain available.
      toast.info("Stopped. Your prompt has been restored.");
    } else {
      // Retry path: leave the original user message in place, clear the
      // in-progress assistant text, and re-surface a retry affordance.
      setMessages((prev) => prev.map((m) =>
        m.id === ctx.assistantId
          ? {
              ...m,
              content: "",
              tools: [],
              generated: [],
              reasoning: null,
              reasoningStreaming: false,
              reasoningDurationMs: null,
              error: { headline: "Stopped.", details: "You stopped the retry. Click Retry to try again.", retryable: true },
            }
          : m
      ));
      toast.info("Stopped.");
    }
  }, [user]);

  // (skill insertion is no longer prompt-based; skills are toggles persisted per conversation)
  const handlePromptSelect = (p: string) => { setInput(p); setActiveTab("chat"); inputRef.current?.focus(); };
  const handleInsightPrompt = (p: string) => { setInput(p); inputRef.current?.focus(); };

  // Resolve full skill objects for the active-skills bar (matched against fetched list)
  const activeSkills = allSkills.filter((s) => selectedSkillIds.includes(s.id));
  const activeKnowledge = allKnowledge.filter((k) => selectedKnowledgeIds.includes(k.id));

  const removeSkill = async (skillId: string) => {
    if (!canEditChatContext) return;
    const scopeToken = loadTokenRef.current;
    const prev = selectedSkillIds;
    setSelectedSkillIds(prev.filter((id) => id !== skillId));
    if (conversationId) {
      const { error } = await awsClient
        .from("conversation_skills")
        .delete()
        .eq("conversation_id", conversationId)
        .eq("skill_id", skillId);
      if (error && scopeToken === loadTokenRef.current) {
        setSelectedSkillIds(prev);
        toast.error("Could not disable skill.");
      }
    }
  };

  const removeKnowledge = async (knowledgeId: string) => {
    if (!canEditChatContext) return;
    const scopeToken = loadTokenRef.current;
    const prev = selectedKnowledgeIds;
    setSelectedKnowledgeIds(prev.filter((id) => id !== knowledgeId));
    if (conversationId) {
      const { error } = await awsClient
        .from("conversation_knowledge")
        .delete()
        .eq("conversation_id", conversationId)
        .eq("knowledge_id", knowledgeId);
      if (error && scopeToken === loadTokenRef.current) {
        setSelectedKnowledgeIds(prev);
        toast.error("Could not disable knowledge pack.");
      }
    }
  };

  const [bundleCounts, setBundleCounts] = useState<Record<string, number>>({});
  const [contextDetailsError, setContextDetailsError] = useState<string|null>(null);
  const [contextDetailsVersion, setContextDetailsVersion] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const current = () => !cancelled;
    setAllSkills([]); setAllKnowledge([]); setBundleCounts({}); setContextDetailsError(null);
    void (async () => {
      if(conversationId && !canEditChatContext){
        const details=await operation("chat_context_details",{conversationId},getCsrfToken());
        if(!current())return;
        setAllSkills(details.skills);setAllKnowledge(details.knowledge);setBundleCounts(details.bundleCounts);setActivePersona(details.persona);
        return;
      }
      const [skills, knowledge, files] = await Promise.all([
        selectedSkillIds.length ? readAllRows<Skill>((from,to)=>awsClient.from("skills").select("id,title,description,icon,category").in("id",selectedSkillIds).order("id").range(from,to),current) : {data:[],error:null},
        selectedKnowledgeIds.length ? readAllRows<KnowledgePack>((from,to)=>awsClient.from("knowledge_packs").select("id,user_id,title,description,content,is_shared").in("id",selectedKnowledgeIds).order("id").range(from,to),current) : {data:[],error:null},
        selectedSkillIds.length ? readAllRows<{skill_id:string}>((from,to)=>awsClient.from("skill_files").select("id,skill_id,kind").in("skill_id",selectedSkillIds).in("kind",["script","asset"]).order("id").range(from,to),current) : {data:[],error:null},
      ]);
      if (!current()) return;
      const error = skills.error ?? knowledge.error ?? files.error;
      if (error) throw new Error(error.message);
      if (!skills.data || !knowledge.data || !files.data) throw new Error("Selected context details could not be loaded.");
      if (skills.data.length !== new Set(selectedSkillIds).size || knowledge.data.length !== new Set(selectedKnowledgeIds).size) throw new Error("Some selected context is unavailable. The conversation owner may need to update its selections.");
      setAllSkills(skills.data); setAllKnowledge(knowledge.data);
      const counts:Record<string,number> = {};
      for (const file of files.data) counts[file.skill_id] = (counts[file.skill_id] ?? 0) + 1;
      setBundleCounts(counts);
    })().catch(error => { if(current()) setContextDetailsError(error instanceof Error ? error.message : "Selected context details could not be loaded."); });
    return () => { cancelled = true; };
  }, [selectedSkillIds,selectedKnowledgeIds,selectedPersonaId,conversationId,canEditChatContext,contextDetailsVersion]);
  const handleRetryMessage = useCallback(async (assistantMessageId: string) => {
    const candidate = lastSendRef.current;
    const last = candidate?.assistantId === assistantMessageId ? candidate : null;
    const restored = durableTurnsRef.current.get(assistantMessageId);
    if ((!last && !restored) || !user || activeTurnRef.current) return;
    if (restored?.reconciliationRequired) { toast.error("This turn requires provider reconciliation before retry."); return; }
    const retryTurnId = restored?.turnId ?? last?.turnId;
    const retryKey = retryTurnId ? `askbell.chat-retry.${retryTurnId}` : null;
    const retryClientRequestId = retryKey ? (globalThis.localStorage.getItem(retryKey) ?? crypto.randomUUID()) : undefined;
    if (retryKey && retryClientRequestId) globalThis.localStorage.setItem(retryKey, retryClientRequestId);
    const retryContext = last ?? { contentParts: [], userText: "", convId: conversationId, apiMessages: [], knowledge: [], request: { conversationId: conversationId ?? undefined, clientRequestId: crypto.randomUUID(), prompt: "retry", attachmentIds: [], modelId: selectedModelId ?? "openai-direct/gpt-6-astra" } as ChatStartInput, turnId: retryTurnId };
    setRetryingMessageId(assistantMessageId);

    // Clear the previous error + tool/content state on the failed assistant bubble
    // and re-stream into the same message id so the UI feels like a true retry.
    setMessages((prev) => prev.map((m) =>
      m.id === assistantMessageId
        ? { ...m, content: "", tools: [], generated: [], error: null }
        : m
    ));

    // Clear any previously-rendered reasoning before the retry stream begins.
    setMessages((prev) => prev.map((m) =>
      m.id === assistantMessageId
        ? { ...m, reasoning: null, reasoningStreaming: false, reasoningDurationMs: null }
        : m
    ));

    const updateAssistant = (fn: (m: ChatMessage) => ChatMessage) => {
      setMessages((prev) => prev.map((m) => m.id === assistantMessageId ? fn(m) : m));
    };

    const turn: ActiveTurn = {
      id: crypto.randomUUID(),
      clientRequestId: retryContext.request.clientRequestId,
      kind: "retry",
      phase: "preparing",
      controller: new AbortController(),
      cancelled: false,
      assistantId: assistantMessageId,
      convId: retryContext.convId,
    };
    activeTurnRef.current = turn;
    streamAbortRef.current = turn.controller;
    setIsStreaming(true);
    let finalAssistantText = "";
    let finalReasoning = "";
    let reasoningStartedAt: number | null = null;
    let finalReasoningDurationMs: number | null = null;
    let sawDone = false;
    let sawError = false;
    let sawAnyEvent = false;
    let sawToolActivity = false;
    let generatedFileCount = 0;
    let durableFinalLoaded = false;

    try {
      if (turn.cancelled) return;
      turn.phase = "streaming";
      const streamOutcome = await streamChatTurn({
        ...chatRetrySubmission(retryContext.request, retryTurnId, retryClientRequestId),
        signal: turn.controller.signal,
        cancelRequested: () => turn.stopRequested === true,
        onTurnId: (id) => { turn.serverTurnId = id; if (last) last.turnId = id; },
        onStarted: (started) => {
          if(turn.cancelled)return;
          setAutoLoadedSkillIds(new Set(started.loadedSkillIds ?? []));
          turn.userMessageRowId = started.messageId;
          turn.convId = started.conversationId;
          retryContext.convId = started.conversationId;
        },
        onFinal: async (result) => {
          if(turn.cancelled)return;
          durableFinalLoaded = true;
          await loadConversation(result.conversationId);
          if (!turn.cancelled) void runCompletedFollowups(result, retryContext.userText, retryContext.request.prompt);
        },
        onEvent: (ev) => {
          if (turn.cancelled) return;
          sawAnyEvent = true;
          if (['model_start','model_end','delta','reasoning_delta','reasoning_start','reasoning','reasoning_end','tool_start','tool_progress','tool_result'].includes(String(ev.type))) updateAssistant(message=>applyChatProgress(message,ev));
          if (["skills_loaded","skill_loaded"].includes(String(ev.type)) && Array.isArray(ev.skillIds)) {
            setAutoLoadedSkillIds(new Set(ev.skillIds));
          } else if (ev.type === "done") {
            sawDone = true;
          } else if (ev.type === "error") {
            sawError = true;
            const headline = typeof ev.message === "string" ? ev.message : "The model couldn't finish responding.";
            updateAssistant((message) => ({ ...message, error: { headline, details: typeof ev.details === "string" ? ev.details : null, retryable: ev.status !== "cancelled" && ev.reviewRequired !== true, recoverableAsJob: ev.reviewRequired !== true } }));
            if (ev.status !== "cancelled") toast.error(headline);
          }
        },
      });
      sawDone = sawDone || streamOutcome.sawDone;
      sawError = sawError || streamOutcome.sawError;
      sawAnyEvent = sawAnyEvent || streamOutcome.sawAnyEvent;

      // Silent-end detection (same logic as handleSend) — surface a retryable
      // error if the stream closed without a `done` or explicit `error` event.
      if (turn.cancelled) {
        // User aborted — handleStop already updated the UI.
      } else if (!sawDone && !sawError) {
        const headline = finalAssistantText.trim()
          ? "The retried response was cut off."
          : "The retried response was cut off before it started.";
        const details = sawAnyEvent
          ? "The server closed the stream without finishing. The request likely hit a processing limit — often a large volume of files or a very long run. Try again, or simplify the request."
          : "The server closed the connection before sending any data. Please retry.";
        updateAssistant((m) => {
          const tools = (m.tools ?? []).map((t) =>
            t.status === "running" ? { ...t, status: "error" as const } : t
          );
          return { ...m, tools, error: { headline, details, retryable: true, recoverableAsJob: true } };
        });
        toast.error(headline);
      }
    } catch (err: any) {
      if (turn.cancelled || err?.name === "AbortError") {
        // Clean user-initiated cancel.
      } else {
        const msg = err?.message || "Something went wrong.";
        updateAssistant((m) => ({ ...m, error: { headline: chatFailureHeadline(Boolean(turn.serverTurnId)), details: msg, retryable: true } }));
        toast.error(msg);
      }
    } finally {
      const stillOwner = activeTurnRef.current?.id === turn.id;
      if (stillOwner) {
        activeTurnRef.current = null;
        streamAbortRef.current = null;
        setIsStreaming(false);
        setRetryingMessageId(null);
      }

    }
  }, [user, loadConversation, runCompletedFollowups]);

  const rerunRequests = useRef(new Map<string,{code:string;requestId:string}>());
  const rerunLocks = useRef(new Set<string>());
  const handleRerunPython = useCallback(
    async (messageId: string, toolId: string, newCode: string) => {
      if (readOnlyChat) return;
      if (!conversationId) { toast.error("Open a saved conversation before running Python."); return; }
      const key = `${user?.audience}:${user?.id}:${conversationId}:${messageId}:${toolId}`;
      if (rerunLocks.current.has(key)) return;
      rerunLocks.current.add(key);
      const scopeToken = loadTokenRef.current;
      const prior = rerunRequests.current.get(key);
      const requestId = prior?.code === newCode ? prior.requestId : crypto.randomUUID();
      rerunRequests.current.set(key,{code:newCode,requestId});
      // Mark tool running and reset its output buffers immediately
      setMessages((prev) => prev.map((m) => {
        if (m.id !== messageId) return m;
        const tools = (m.tools ?? []).map((t) => {
          if (t.id !== toolId) return t;
          return {
            ...t,
            status: "running",
            args: { ...(t.args ?? {}), code: newCode, edited: true },
            result: { code: newCode, stdout: "", stderr: "", files: [] },
          } as ToolEvent;
        });
        return { ...m, tools };
      }));

      const finalize = (result: any) => {
        if (scopeToken !== loadTokenRef.current) return;
        setMessages((prev) => prev.map((m) => {
          if (m.id !== messageId) return m;
          const tools = (m.tools ?? []).map((t) => {
            if (t.id !== toolId) return t;
            return { ...t, status: result?.error ? "error" : "done", result } as ToolEvent;
          });
          const generated = [...(m.generated ?? [])];
          if (Array.isArray(result?.files)) {
            for (const f of result.files) {
              if (f?.storage_path && f?.filename) {
                generated.push({
                  filename: f.filename,
                  mime_type: f.mime_type,
                  size: f.size ?? 0,
                  storage_path: f.storage_path,
                  signed_url: f.signed_url,
                  pinned: !!f.pinned,
                });
              }
            }
          }
          return { ...m, tools, generated };
        }));
      };

      try {
        const started = await operation<{requestId:string;conversationId:string;code:string},{jobId:string}>(
          "python_rerun_start", {requestId,conversationId,code:newCode}, getCsrfToken());
        const deadline = Date.now() + 9 * 60_000;
        while (Date.now() < deadline) {
          const state = await operation<{jobId:string},{status:string;result?:any;error?:string}>("python_rerun_state", {jobId:started.jobId}, getCsrfToken());
          if (state.status === "succeeded") { rerunRequests.current.delete(key); finalize(state.result); return; }
          if (["failed","partial","cancelled"].includes(state.status)) {
            rerunRequests.current.delete(key);
            const error = state.error || state.result?.error || "Python execution stopped. Saved output is retained.";
            finalize({...state.result,code:newCode,error});
            toast.error(error);
            return;
          }
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        throw new Error("Execution is still pending. Generated files will appear in this conversation when it completes.");
      } catch (err: any) {
        const message = err?.message || "Python re-run failed.";
        toast.error(message);
        finalize({code:newCode,stdout:"",stderr:"",files:[],error:message});
      } finally { rerunLocks.current.delete(key); }
    },
    [conversationId, readOnlyChat, user?.id, user?.audience],
  );

  const [promptPickerOpen, setPromptPickerOpen] = useState(false);
  const [personaPickerOpen, setPersonaPickerOpen] = useState(false);
  const [activePersona, setActivePersona] = useState<Pick<Persona,"id"|"name"> | null>(null);

  // Hydrate the active persona row whenever the selection changes so the chip can show its name.
  useEffect(() => {
    let cancelled = false;
    if (!selectedPersonaId) {
      setActivePersona(null);
      return;
    }
    if(conversationId && !canEditChatContext)return;
    // Avoid an extra fetch if we already have the right one.
    if (activePersona?.id === selectedPersonaId) return;
    awsClient
      .from("personas")
      .select("id, user_id, name, description, system_prompt, is_shared")
      .eq("id", selectedPersonaId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn("active persona fetch", error);
          return;
        }
        setActivePersona((data as Persona | null) ?? null);
      });
    return () => { cancelled = true; };
  }, [selectedPersonaId, activePersona?.id,conversationId,canEditChatContext]);

  // Persist persona choice when user picks/clears from the inline chip picker.
  // Mirrors the logic in PersonasPanel.select so behavior stays consistent.
  const handlePersonaPick = async (personaId: string | null) => {
    if (!canEditChatContext) return;
    const scopeToken = loadTokenRef.current;
    if (!user) return;
    const prev = selectedPersonaId;
    userTouchedPersonaRef.current = true;
    setSelectedPersonaId(personaId);
    if (!conversationId) return; // persisted on first send
    if (personaId === null) {
      const { error } = await awsClient
        .from("conversation_personas")
        .delete()
        .eq("conversation_id", conversationId);
      if (error && scopeToken === loadTokenRef.current) {
        setSelectedPersonaId(prev);
        toast.error("Could not clear persona.");
      }
      return;
    }
    const { error } = await awsClient
      .from("conversation_personas")
      .upsert(
        {
          conversation_id: conversationId,
          persona_id: personaId,
          user_id: user.id,
          enabled_at: new Date().toISOString(),
        },
        { onConflict: "conversation_id" },
      );
    if (error && scopeToken === loadTokenRef.current) {
      setSelectedPersonaId(prev);
      toast.error("Could not select persona.");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "ArrowUp" && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey && input.length === 0 && !promptPickerOpen) {
      e.preventDefault();
      setPromptPickerOpen(true);
      return;
    }
    if (e.key === "Enter" && (e.altKey || e.ctrlKey)) {
      e.preventDefault();
      const ta = e.currentTarget;
      const start = ta.selectionStart ?? ta.value.length;
      const end = ta.selectionEnd ?? ta.value.length;
      const next = ta.value.slice(0, start) + "\n" + ta.value.slice(end);
      setInput(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 1;
      });
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const PASTE_INLINE_MAX = 5000;
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // 1) Pasted files (e.g. a screenshot copied from another app, or a file
    // copied from the OS file manager) — attach them like a normal upload.
    // Browsers do NOT auto-attach clipboard files to a textarea, so we have
    // to intercept here or the paste is silently dropped.
    const cbFiles = e.clipboardData.files;
    if (cbFiles && cbFiles.length > 0) {
      e.preventDefault();
      const files = Array.from(cbFiles).map((f) => {
        // Screenshots from many OSes paste as type "image/png" with a generic
        // name like "image.png". Give them a timestamped filename so multiple
        // pastes in one message don't collide and are easy to identify later.
        if (!f.name || /^image\.(png|jpe?g|webp|gif)$/i.test(f.name)) {
          const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
          const ts = new Date().toISOString().replace(/[:T]/g, "-").replace(/\..+$/, "");
          return new File([f], `pasted-${ts}.${ext}`, { type: f.type });
        }
        return f;
      });
      handleFiles(files);
      const imgCount = files.filter((f) => f.type.startsWith("image/")).length;
      toast.success(
        imgCount === files.length && imgCount > 0
          ? `Pasted ${imgCount} image${imgCount === 1 ? "" : "s"}`
          : `Pasted ${files.length} file${files.length === 1 ? "" : "s"}`,
      );
      return;
    }

    // 2) Pasted long text — convert to a .txt attachment so the textarea
    // doesn't get flooded with a wall of text.
    const text = e.clipboardData.getData("text/plain");
    if (!text || text.length <= PASTE_INLINE_MAX) return;
    e.preventDefault();
    const ts = new Date()
      .toISOString()
      .replace(/[:T]/g, "-")
      .replace(/\..+$/, "");
    const filename = `pasted-${ts}.txt`;
    const file = new File([text], filename, { type: "text/plain" });
    handleFiles([file]);
    toast.success(`Pasted text saved as ${filename}`, {
      description: `${text.length.toLocaleString()} characters attached as a file.`,
    });
  };

  const ChatBody = (
    <>
      <div
        ref={scrollViewportRef}
        className="flex-1 overflow-y-auto px-4 py-6"
        style={{ overflowAnchor: "none" }}
      >
        <div ref={scrollContentRef} style={{ overflowAnchor: "none" }}>
        {loadingConvId && messages.length === 0 ? (
          <div className="mx-auto max-w-3xl space-y-4 py-10">
            <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
            <div className="h-24 w-full animate-pulse rounded-ui-md bg-muted/60" />
            <div className="h-4 w-1/4 animate-pulse rounded bg-muted" />
            <div className="h-40 w-full animate-pulse rounded-ui-md bg-muted/60" />
            <p className="text-center text-ui-sm text-muted-foreground">Opening conversation…</p>
          </div>
        ) : conversationLoadError ? (
          <div role="alert" className="mx-auto my-12 max-w-xl rounded-ui-md border border-border bg-card p-8 text-center ">
            <h2 className="text-ui-xl font-bold">Couldn’t open this conversation</h2>
            <p className="mt-3 text-ui-base text-muted-foreground">{conversationLoadError}</p>
            <div className="mt-6 flex justify-center gap-3">
              <Button variant="outline" onClick={()=>{if(conversationId)void loadConversation(conversationId);}}>Try again</Button>
              <Button onClick={handleNewChat}>New chat</Button>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <ChatEmptyState
            onPromptClick={(prompt) => {
              setInput(prompt);
              inputRef.current?.focus();
            }}
          />
        ) : (
          <ChatMessages
            messages={messages}
            isStreaming={isStreaming}
            onRerunPython={readOnlyChat ? undefined : handleRerunPython}
            onRetryMessage={readOnlyChat ? undefined : handleRetryMessage}
            retryingMessageId={retryingMessageId}
            onRunFailedAsJob={readOnlyChat ? undefined : handleRunFailedAsJob}
            runFailedAsJobPendingId={runFailedAsJobPendingId}
          />
        )}
        {jobRunIds.map((rid) => (
          <JobProgressCard
            key={rid}
            runId={rid}
            onDismiss={() => dismissJob(rid)}
            clearFinishedToken={clearFinishedJobsToken}
            onIngested={({ message, files }) => {
              // Drop the finished job's write-up into the conversation so the
              // user can keep chatting about it (and its files).
              setMessages((prev) =>
                prev.some((m) => m.id === message.id)
                  ? prev
                  : [
                      ...prev,
                      {
                        id: message.id,
                        role: "assistant" as const,
                        content: message.content,
                        created_at: message.created_at,
                        generated: files,
                      },
                    ],
              );
              setWorkspaceRefreshKey((v) => v + 1);
              setHistoryVersion((v) => v + 1);
            }}
          />
        ))}
        <div ref={bottomRef} style={{ overflowAnchor: "none" }} />
        </div>
      </div>

      <div className="border-t border-border p-4">
        {isStreaming && streamElapsed >= 60 && (
          <div className="mx-auto mb-2 max-w-3xl text-ui-sm text-muted-foreground">
            {streamElapsed < 180
              ? `Still working… (${streamElapsed}s elapsed — long answers can take a few minutes)`
              : streamElapsed < 330
                ? `Still thinking… (${Math.floor(streamElapsed / 60)}m ${streamElapsed % 60}s). Complex Python or research can push to ~6 minutes.`
                : `Approaching the per-turn limit (${Math.floor(streamElapsed / 60)}m ${streamElapsed % 60}s). If this times out, click Retry or start a fresh message with a narrower ask.`}
          </div>
        )}
        {readOnlyChat ? (
          <div className="flex items-center gap-2 rounded-ui-md border border-border bg-secondary/40 px-4 py-3 text-ui-base text-muted-foreground">
            <Eye className="h-4 w-4 shrink-0" />
            <span>{isViewer?"View-only — ask the owner for reply access to send messages here.":roleLoading?"Checking conversation access…":roleError??"Reply access is unavailable."}</span>
            {roleError && <Button variant="outline" size="sm" onClick={refreshRole}>Try again</Button>}
          </div>
        ) : (
        <form onSubmit={handleSend} className="w-full">
          {contextDetailsError && <div role="alert" className="mb-2 flex items-center justify-between gap-3 rounded-ui-sm border border-destructive/30 bg-destructive/5 px-3 py-2 text-ui-sm"><span>{contextDetailsError}</span><Button size="sm" variant="outline" onClick={() => setContextDetailsVersion(v=>v+1)}>Retry</Button></div>}
          <ActiveSkillsBar
            canEditContext={canEditChatContext}
            skills={activeSkills}
            bundleCounts={bundleCounts}
            autoLoadedIds={autoLoadedSkillIds}
            autoMode={skillMode === "auto"}
            onRemove={removeSkill}
            onOpenPanel={() => setActiveTab("skills")}
          />
          <ActiveKnowledgeBar
            canEditContext={canEditChatContext}
            packs={activeKnowledge}
            onRemove={removeKnowledge}
            onOpenPanel={() => setActiveTab("knowledge")}
          />
          {workflowSuggestion.suggestion && (
            <WorkflowSuggestionChip
              suggestion={workflowSuggestion.suggestion}
              starting={startingWorkflow}
              onRunAsJob={handleRunAsJob}
              onContinueInline={workflowSuggestion.dismiss}
              onDismiss={workflowSuggestion.dismiss}
            />
          )}
          <AttachmentChips
            items={pending}
            onRemove={(id) => void removePending(id)}
            onRetryExtraction={handleRetryExtraction}
          />
          {isMobile && (
            <div className="flex justify-end items-center gap-2 mb-2">
              <div className="relative flex-1 min-w-0 max-w-[60%]">
                <PersonaChip
                  triggerRef={personaTriggerRef}
                  pickerOpen={canEditChatContext && personaPickerOpen}
                  persona={activePersona}
                  onOpenPicker={() => { if (canEditChatContext) setPersonaPickerOpen(true); }}
                  onClear={() => { handlePersonaPick(null); personaTriggerRef.current?.focus(); }}
                  className="w-full"
                  disabled={!canEditChatContext || isStreaming || !!loadingConvId || !!conversationLoadError}
                />
                <PersonaQuickPicker
                  open={canEditChatContext && personaPickerOpen}
                  onClose={() => { setPersonaPickerOpen(false); personaTriggerRef.current?.focus(); }}
                  selectedPersonaId={selectedPersonaId}
                  onSelect={handlePersonaPick}
                />
              </div>
              <ModelModeToggle
                value={modelMode}
                disabled={isStreaming || !!loadingConvId || !!conversationLoadError}
                selectedModelId={selectedModelId}
                onModelChange={async (nextId) => {
                    const scopeToken = loadTokenRef.current;
                  const prev = selectedModelId;
                  setSelectedModelId(nextId);
                  if (conversationId) {
                    const { error } = await awsClient
                      .from("conversations")
                      .update({ model_id: nextId })
                      .eq("id", conversationId);
                    if (error && scopeToken === loadTokenRef.current) {
                      setSelectedModelId(prev);
                      toast.error("Could not change model.");
                    }
                  }
                }}
                onChange={async (next) => {
                    const scopeToken = loadTokenRef.current;
                  setModelMode(next);
                  if (conversationId) {
                    const { error } = await awsClient
                      .from("conversations")
                      .update({ model_mode: next })
                      .eq("id", conversationId);
                    if (error && scopeToken === loadTokenRef.current) {
                      setModelMode(modelMode);
                      toast.error("Could not change model mode.");
                    }
                  }
                }}
              />
            </div>
          )}
          {currentProject && messages.length === 0 && (
            <div className="mb-2 flex justify-center">
              <Link
                to="/projects/$projectId"
                params={{ projectId: currentProject.id }}
                className="inline-flex items-center gap-1.5 text-ui-sm px-2.5 py-1 rounded-ui-sm bg-primary/10 text-primary hover:bg-primary/15 transition-colors max-w-[260px]"
                title={`Chatting in project: ${currentProject.name}`}
              >
                <FolderOpen className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  In project: <span className="font-bold">{currentProject.name}</span>
                </span>
              </Link>
            </div>
          )}
          {pending.some(item => item.uploaded?.mime_type.startsWith("image/")) && (
            <p className="mb-2 text-ui-sm text-muted-foreground">Large images are resized for vision. Your original files remain available.</p>
          )}
          <div className="flex gap-1.5 md:gap-2 items-stretch">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.md,.markdown,.json,.xml,.html,.htm,.css,.js,.jsx,.ts,.tsx,.py,.cpp,.c,.h,.hpp,.java,.go,.rs,.rb,.swift,.kt,.cs,.php,.sh,.bash,.zsh,.sql,.yaml,.yml,.toml,.ini,.cfg,.conf,.log,.r,.R,.sas,.sps,.do,.rtf,.tex,.lua,.pl,.pm,.scala,.zig,.dart,.m,.mm,.f90,.f95,.proto,.graphql,.vue,.svelte,.ps1,.bat,.cmd"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length) handleFiles(files);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => fileInputRef.current?.click()}
              className="self-end shrink-0 h-10 w-10 md:h-11 md:w-11 rounded-ui-md"
              aria-label="Attach files"
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            <div className="flex-1 relative min-w-0">
              <PromptQuickPicker
                open={promptPickerOpen}
                onClose={() => { setPromptPickerOpen(false); inputRef.current?.focus(); }}
                onSelect={(content) => {
                  setInput(content);
                  requestAnimationFrame(() => {
                    const ta = inputRef.current;
                    if (ta) {
                      ta.style.height = "auto";
                      ta.style.height = `${ta.scrollHeight}px`;
                      ta.focus();
                      ta.selectionStart = ta.selectionEnd = content.length;
                    }
                  });
                }}
              />
              <textarea
                ref={(el) => {
                  inputRef.current = el;
                  if (el) {
                    // Auto-grow: reset then size to scrollHeight (CSS maxHeight caps it).
                    el.style.height = "auto";
                    el.style.height = `${el.scrollHeight}px`;
                  }
                }}
                data-tour-chat-input=""
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder="Ask anything…"
                rows={1}
                className="w-full resize-none rounded-ui-md border border-input bg-secondary/50 px-4 py-3 pr-12 text-ui-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring overflow-y-auto md:min-h-[72px]"
                style={{ maxHeight: "200px" }}
              />
            </div>
            {!isMobile && (
              <div className="flex flex-col items-stretch justify-between gap-1.5 shrink-0">
                <div className="relative">
                  <PersonaChip
                    triggerRef={personaTriggerRef}
                    pickerOpen={canEditChatContext && personaPickerOpen}
                    persona={activePersona}
                    onOpenPicker={() => { if (canEditChatContext) setPersonaPickerOpen(true); }}
                    onClear={() => { handlePersonaPick(null); personaTriggerRef.current?.focus(); }}
                    className="w-full"
                    disabled={!canEditChatContext || isStreaming || !!loadingConvId || !!conversationLoadError}
                  />
                  <PersonaQuickPicker
                    open={canEditChatContext && personaPickerOpen}
                    onClose={() => { setPersonaPickerOpen(false); personaTriggerRef.current?.focus(); }}
                    selectedPersonaId={selectedPersonaId}
                    onSelect={handlePersonaPick}
                  />
                </div>
                <ModelModeToggle
                  value={modelMode}
                  disabled={isStreaming || !!loadingConvId || !!conversationLoadError}
                  selectedModelId={selectedModelId}
                  onModelChange={async (nextId) => {
                    const scopeToken = loadTokenRef.current;
                    const prev = selectedModelId;
                    setSelectedModelId(nextId);
                    if (conversationId) {
                      const { error } = await awsClient
                        .from("conversations")
                        .update({ model_id: nextId })
                        .eq("id", conversationId);
                      if (error && scopeToken === loadTokenRef.current) {
                        setSelectedModelId(prev);
                        toast.error("Could not change model.");
                      }
                    }
                  }}
                  onChange={async (next) => {
                    const scopeToken = loadTokenRef.current;
                    setModelMode(next);
                    if (conversationId) {
                      const { error } = await awsClient
                        .from("conversations")
                        .update({ model_mode: next })
                        .eq("id", conversationId);
                      if (error && scopeToken === loadTokenRef.current) {
                        setModelMode(modelMode);
                        toast.error("Could not change model mode.");
                      }
                    }
                  }}
                />
              </div>
            )}
            {isStreaming || isStopping ? (
              <Button
                type="button"
                size="icon"
                variant="destructive"
                onClick={handleStop}
                disabled={isStopping}
                title={isStopping ? "Stopping…" : "Stop generating"}
                aria-label={isStopping ? "Stopping" : "Stop generating"}
                className={`self-end shrink-0 h-10 w-10 md:h-11 md:w-11 rounded-ui-md transition-all ${isStopping ? "opacity-70 scale-95" : ""}`}
              >
                <Square className="h-4 w-4" fill="currentColor" />
              </Button>
            ) : (
              <Button
                type="submit"
                size="icon"
                disabled={!!loadingConvId || !!conversationLoadError || (!input.trim() && !pending.some((p) => p.uploaded || (p.workspaceImport && !p.uploading && !p.error)))}
                className="self-end shrink-0 h-10 w-10 md:h-11 md:w-11 rounded-ui-md"
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </div>
        </form>
        )}
      </div>
    </>
  );

  return (
    <AppLayout>
      <div className="flex-1 flex flex-col min-h-0">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="relative flex-1 flex flex-col min-h-0"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="border-b border-border px-2 md:px-4 flex items-center justify-between gap-2 shrink-0">
            <div className="flex-1 min-w-0 overflow-x-auto scrollbar-none">
              <TabsList className="bg-transparent h-10 w-max">
                <TabsTrigger value="chat" aria-label="Chat" className="gap-1.5 data-[state=active]:bg-secondary">
                  <Sparkles className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Chat</span>
                </TabsTrigger>
                <TabsTrigger value="skills" aria-label="Skills" data-tour-skills-tab="" className="gap-1.5 data-[state=active]:bg-secondary">
                  <BookOpen className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Skills</span>
                  <WhatsThis title="What are Skills?" ariaLabel="What are Skills?">
                    Skills are pre-built how-to guides AskBell follows for specific tasks (e.g. RFP review, data analysis). Most useful ones are on by default — you don't need to pick.
                  </WhatsThis>
                </TabsTrigger>
                <TabsTrigger value="knowledge" aria-label="Knowledge Packs" data-tour-knowledge-tab="" className="gap-1.5 data-[state=active]:bg-secondary">
                  <BookMarked className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Knowledge Packs</span>
                  <WhatsThis title="What are Knowledge Packs?" ariaLabel="What are Knowledge Packs?">
                    Reusable reference material — like a brand brief or industry overview — that AskBell keeps in mind for every reply in this chat.
                  </WhatsThis>
                </TabsTrigger>
                <TabsTrigger value="personas" aria-label="Personas" data-tour-personas-tab="" className="gap-1.5 data-[state=active]:bg-secondary">
                  <Drama className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Personas</span>
                  <WhatsThis title="What are Personas?" ariaLabel="What are Personas?">
                    A persona swaps AskBell's default voice for a custom one — useful for role-play, expert framing, or specific tones. One persona per chat.
                  </WhatsThis>
                </TabsTrigger>
                <TabsTrigger value="prompts" aria-label="Prompts" className="gap-1.5 data-[state=active]:bg-secondary">
                  <BookOpen className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Prompts</span>
                  <WhatsThis title="What are Prompts?" ariaLabel="What are Prompts?">
                    Saved messages you find yourself sending often. Click one to drop it into the chat box, edit, and send.
                  </WhatsThis>
                </TabsTrigger>
              </TabsList>
            </div>
            <div className="flex items-center gap-1 md:gap-2 shrink-0">
              {isMobile && activeTab === "chat" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setInsightsSheetOpen(true)}
                  className="h-8 w-8 p-0"
                  title="Open insights"
                  aria-label="Open insights"
                >
                  <PanelRight className="h-4 w-4" />
                </Button>
              )}
              {currentProject && (
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: currentProject.id }}
                  className="hidden sm:inline-flex items-center gap-1.5 text-ui-sm px-2 py-1 rounded-ui-sm bg-primary/10 text-primary hover:bg-primary/15 transition-colors max-w-[180px]"
                  title={currentProject.name}
                >
                  <FolderOpen className="h-3 w-3 shrink-0" />
                  <span className="truncate">{currentProject.name}</span>
                </Link>
              )}
              {(messages.length > 0 || conversationId) && (
                <Button size="sm" variant="ghost" onClick={handleNewChat} className="gap-1.5 h-8 px-2">
                  <Plus className="h-3.5 w-3.5" /> <span className="hidden sm:inline">New chat</span>
                </Button>
              )}
              {conversationId && chatRole === "owner" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShareOpen(true)}
                  className="gap-1.5 h-8 px-2"
                  title="Share this chat"
                >
                  <Share2 className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Share</span>
                </Button>
              )}
              {conversationId && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="More chat actions">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => setMovePickerOpen(true)} className="gap-2">
                      <FolderInput className="h-4 w-4" />
                      {currentProject ? "Move to project…" : "Add to project…"}
                    </DropdownMenuItem>
                    {currentProject && (
                      <DropdownMenuItem onClick={() => moveConversationToProject(null)} className="gap-2">
                        <X className="h-4 w-4" />
                        Remove from project
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          <TabsContent value="chat" className="flex-1 min-h-0 mt-0 data-[state=active]:flex w-full">
            <div className="relative flex h-full w-full">
              {isMobile ? (
                <div className="flex flex-col h-full w-full min-h-0">{ChatBody}</div>
              ) : rightPaneCollapsed ? (
                <div className="relative flex h-full w-full min-h-0">
                  <div className="flex flex-col h-full min-h-0 flex-1">{ChatBody}</div>
                  <button
                    type="button"
                    className="bri-shell-handle bri-shell-handle-right"
                    style={{ right: 0 }}
                    onClick={() => setRightPaneCollapsed(false)}
                    aria-label="Context panel"
                    aria-expanded="false"
                    aria-controls="askbell-context-panel"
                  >
                    <ChevronLeft />
                    <span className="bri-shell-tip" aria-hidden="true">Show details</span>
                  </button>
                </div>
              ) : (
                <ResizablePanelGroup
                  orientation="horizontal"
                  className="h-full w-full"
                  defaultLayout={{
                    "chat-content-panel": 100 - rightPaneSizeRef.current,
                    "chat-context-panel": rightPaneSizeRef.current,
                  }}
                  onLayoutChanged={(layout, meta) => {
                    if (!meta.isUserInteraction) return;
                    const nextSize = layout["chat-context-panel"];
                    if (!Number.isFinite(nextSize)) return;
                    rightPaneSizeRef.current = nextSize;
                    try { localStorage.setItem(RIGHT_PANE_SIZE_STORAGE_KEY, String(nextSize)); } catch { /* ignore */ }
                  }}
                >
                  <ResizablePanel id="chat-content-panel" defaultSize="75%" minSize="50%">
                    <div className="flex flex-col h-full min-h-0">{ChatBody}</div>
                  </ResizablePanel>
                  <ResizableHandle />
                  <ResizablePanel
                    id="chat-context-panel"
                    defaultSize={`${rightPaneSizeRef.current}%`}
                    minSize={`${RIGHT_PANE_MIN_SIZE}%`}
                    maxSize={`${RIGHT_PANE_MAX_SIZE}%`}
                    style={{ overflow: "visible" }}
                  >
                    <RightPane
                      conversationId={conversationId}
                      workspaceRefreshKey={workspaceRefreshKey}
                      workspaceSelectedFolderId={workspaceSelectedFolderId}
                      onWorkspaceSelectedFolderChange={setWorkspaceSelectedFolderId}
                      workspaceRevealNodeId={workspaceRevealNodeId}
                      messages={messages}
                      insights={insights}
                      insightLoading={insightLoading}
                      insightError={insightError}
                      onInsightRetry={retryInsights}
                      tokensUsed={tokensUsed}
                      onPromptClick={handleInsightPrompt}
                      onCollapse={() => setRightPaneCollapsed(true)}
                    />
                  </ResizablePanel>
                </ResizablePanelGroup>
              )}

            </div>
          </TabsContent>

          <TabsContent value="skills" className="flex-1 overflow-y-auto mt-0 p-4">
            <SkillsPanel
              canEditContext={canEditChatContext}
              conversationId={conversationId}
              selectedSkillIds={selectedSkillIds}
              onChange={setSelectedSkillIds}
              skillMode={skillMode}
              onSkillModeChange={setSkillMode}
            />
          </TabsContent>

          <TabsContent value="knowledge" className="flex-1 overflow-y-auto mt-0 p-4">
            <KnowledgePanel
              canEditContext={canEditChatContext}
              conversationId={conversationId}
              selectedKnowledgeIds={selectedKnowledgeIds}
              onChange={setSelectedKnowledgeIds}
            />
          </TabsContent>

          <TabsContent value="personas" className="flex-1 overflow-y-auto mt-0 p-4">
            <PersonasPanel
              canEditContext={canEditChatContext}
              conversationId={conversationId}
              selectedPersonaId={selectedPersonaId}
              onChange={(id) => {
                userTouchedPersonaRef.current = true;
                setSelectedPersonaId(id);
              }}
            />
          </TabsContent>

          <TabsContent value="prompts" className="flex-1 overflow-y-auto mt-0 p-4">
            <PromptsPanel onSelect={handlePromptSelect} />
          </TabsContent>

          {isDragging && (
            <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-primary/10 backdrop-blur-sm ring-2 ring-inset ring-primary" role="status" aria-live="polite">
              <div className="flex items-center gap-3 rounded-ui-md border-2 border-dashed border-primary bg-background/90 px-8 py-6 text-ui-lg font-bold text-primary">
                <Paperclip className="h-5 w-5" />
                Drop files or folders to attach
              </div>
            </div>
          )}

        </Tabs>
      </div>

      <ProjectPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        currentProjectId={pendingProjectId ?? null}
        onPick={(projectId) => {
          const scopeToken = loadTokenRef.current, pick = ++projectPickRef.current;
          setPendingProjectId(projectId);
          if (projectId) {
            awsClient.from("projects").select("id, name, default_persona_id").eq("id", projectId).maybeSingle()
              .then(({ data, error }) => {
                if (scopeToken !== loadTokenRef.current || pick !== projectPickRef.current) return;
                if (error || !data) { setPendingProjectId(null); setCurrentProject(null); toast.error("Could not load the selected project. Select it again to retry."); return; }
                setCurrentProject({ id: (data as any).id, name: (data as any).name });
                const defaultPersonaId = (data as any).default_persona_id ?? null;
                if (defaultPersonaId && !userTouchedPersonaRef.current) {
                  setSelectedPersonaId(defaultPersonaId);
                }
              });
          } else {
            setCurrentProject(null);
          }
        }}
      />

      <ProjectPickerDialog
        open={movePickerOpen}
        onOpenChange={setMovePickerOpen}
        currentProjectId={currentProject?.id ?? null}
        onPick={(projectId) => { void moveConversationToProject(projectId); }}
      />

      <Sheet open={insightsSheetOpen} onOpenChange={setInsightsSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="px-4 py-3 border-b border-border shrink-0">
            <SheetTitle>Insights</SheetTitle>
          </SheetHeader>
          <div className="flex-1 min-h-0 overflow-hidden">
            <RightPane
              conversationId={conversationId}
              workspaceRefreshKey={workspaceRefreshKey}
              workspaceSelectedFolderId={workspaceSelectedFolderId}
              onWorkspaceSelectedFolderChange={setWorkspaceSelectedFolderId}
              workspaceRevealNodeId={workspaceRevealNodeId}
              messages={messages}
              insights={insights}
              insightLoading={insightLoading}
              insightError={insightError}
              onInsightRetry={retryInsights}
              tokensUsed={tokensUsed}
              onPromptClick={(text) => {
                handleInsightPrompt(text);
                setInsightsSheetOpen(false);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>

      {conversationId && (
        <ShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          resourceType="chat"
          resourceId={conversationId}
        />
      )}
    </AppLayout>
  );
}


